import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLayout } from '../../../context/LayoutContext';
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
  Autocomplete,
  Breadcrumbs,
  Link,
  // Add these imports:
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  Select as MuiSelect,
  MenuItem as MuiMenuItem,
  InputLabel as MuiInputLabel,
  FormControl as MuiFormControl,
  Tab,
  Tabs,
  Checkbox,
  Alert as MuiAlert,
  Paper as MuiPaper  

} from '@mui/material';
import {
  ArrowBackIos,
  ArrowForwardIos,
  Today,
  Edit,
  Delete,
  Email,
  Send,
  DesktopWindows,
  Language,
  FilterList,
  FilterAlt,
  CalendarMonth,
  NavigateNext,
  Print,
  // Add these icons:
  Add,
  AccessTime,
  Schedule,
  Person,
  Group,
  WatchLater,
  LocalHospital,
  Info,
  ArrowBack,
  ArrowForward,
  CalendarToday,
  DragIndicator,
  NavigateBefore,
  NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useGetWeeklyShiftsByStaffQuery,
  useGetAvailableShiftsQuery,
  useAssignStaffToShiftMutation,
  useReOpenShiftMutation,
  useUpdateShiftMutation
} from '../../../slices/shiftApiSlice';
import { useGetUsersQuery } from '../../../slices/usersApiSlice';
import {
  useGetWeeklyAvailabilitiesByStaffQuery,
  useCreateAvailabilityMutation,
  useUpdateAvailabilityMutation,
  useDeleteAvailabilityMutation
} from '../../../slices/availabilityApiSlice';
import { useSelector } from 'react-redux';
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';
import { CATEGORY_OF_STAFF, REASON_FOR_REQUEST } from '../../../utils/enums';
import { getLocalDateString } from '../../../utils/dateUtils';


// Add this style object near the top of your component (around line 40-50)
const scrollableTableStyle = {
  maxHeight: '70vh', // Adjust height as needed - 70% of viewport height
  overflowY: 'auto',
  border: '1px solid #ddd',
  borderRadius: '4px',
  position: 'relative',
};

// Also add a fixed header style
const fixedHeaderStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};


// Add print styles component
const PrintStyles = () => (
  <style>{`
    @media print {
      .no-print {
        display: none !important;
      }
      .print-page {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      @-moz-document url-prefix() {
        .shift-card, .availability-card, .status-chip, .header-cell {
          box-shadow: inset 0 0 0 1000px var(--print-color) !important;
        }
      }
      body {
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
      }
      .print-container {
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .calendar-table {
        width: 100% !important;
        table-layout: fixed !important;
        border-collapse: collapse !important;
      }
      .shift-text, .availability-text {
        color: #000 !important;
        font-weight: bold !important;
      }
      .print-text {
        font-size: 11pt !important;
        line-height: 1.4 !important;
      }
      @page {
        margin: 0.5in !important;
      }
      .staff-row {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .print-header {
        text-align: center !important;
        margin-bottom: 20px !important;
        border-bottom: 2px solid #000 !important;
        padding-bottom: 10px !important;
      }
      .shift-assigned {
        background-color: #4caf50 !important;
      }
      .shift-weekend-assigned {
        background-color: #ff9800 !important;
      }
      .shift-agency {
        background-color: #9c27b0 !important;
      }
      .shift-cancelled {
        background-color: #f44336 !important;
      }
      .shift-available {
        background-color: #2196f3 !important;
      }
      .availability-pending {
        background-color: #e3f2fd !important;
      }
      .availability-approved {
        background-color: #e8f5e8 !important;
      }
      .availability-rejected {
        background-color: #ffebee !important;
      }
    }
  `}</style>
);

// Shift time presets (from StaffAvailability)
const SHIFT_PRESETS = {
  'LD': { start: '08:00', end: '20:00', label: 'Long Day (8:00-20:00)' },
  'ND': { start: '20:00', end: '08:00', label: 'Night Duty (20:00-8:00)' },
  'AM': { start: '08:00', end: '16:00', label: 'Morning (8:00-16:00)' },
  'PM': { start: '14:00', end: '20:00', label: 'Afternoon (14:00-20:00)' },
  'TW': { start: '20:00', end: '00:00', label: 'Twilight (20:00-00:00)' },
  'ELD': { start: '07:30', end: '20:30', label: 'Extended Long Day (7:30-20:30)' },
  'ESCORT': { start: '08:00', end: '20:00', label: 'Escort (8:00-20:00)' },
};

// Unavailability reasons (from StaffAvailability)
const UNAVAILABILITY_REASONS = [
'Annual Leave',
'Adoptive Leave',
'Bereavement Leave',
'Carers Leave.',
'Certified Sick Leave',
'Force Majeure',
'Health and Safety Leave',
'Jury Leave',
'Maternity Leave',
'Other',
'Parents Leave',
'Parental Leave',
'Paternity Leave',
'Sick Leave',
'Study Leave / Mandatory Training',
];




// Color coding for different shift statuses
const SHIFT_STATUS_COLORS = {
  assigned: '#4caf50',
  available: '#2196f3',
  weekendAssigned: '#ff9800',
  weekendAvailable: '#ffb74d',
  agency: '#9c27b0',
  cancelled: '#f44336',
  sickLeave: '#ff5252',
  sickLeavePartial: '#ff9800',
};

// Helper function to format hours and minutes
const formatHoursMinutes = (totalMinutes) => {
  if (!totalMinutes || totalMinutes === 0) return '0h 0m';
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};

// Helper function to format time
const formatTime = (dateTime) => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Helper function to compare objects deeply
const isEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};



// Remove this entire function (around line 247):
// const getLocalDateString = (date) => {
//   const d = new Date(date);
//   return d.toISOString().split('T')[0];
// };

const StaffWeeklyView = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.auth);

  // Existing state
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [staffList, setStaffList] = useState([]);
  const [filteredStaffList, setFilteredStaffList] = useState([]);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [filteredAvailableShifts, setFilteredAvailableShifts] = useState([]);
  const [staffShifts, setStaffShifts] = useState({});
  const [staffAvailabilities, setStaffAvailabilities] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [shiftToRelease, setShiftToRelease] = useState(null);
  const [shiftToEdit, setShiftToEdit] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedStaffForEmail, setSelectedStaffForEmail] = useState(null);
  const [emailContent, setEmailContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMethod, setEmailMethod] = useState('outlook');
  const [includeWeekend, setIncludeWeekend] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [onlyShowAvailableShifts, setOnlyShowAvailableShifts] = useState(false);
  const [selectedWardFilter, setSelectedWardFilter] = useState('all');
  const [selectedJobTitleFilter, setSelectedJobTitleFilter] = useState('all');
  const [availableWards, setAvailableWards] = useState([]);
  const [availableJobTitles, setAvailableJobTitles] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // NEW: Availability management state (from StaffAvailability)
  const [selectedPreset, setSelectedPreset] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState(null);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedDayForAvailability, setSelectedDayForAvailability] = useState(null);
  const [selectedStaffForAvailability, setSelectedStaffForAvailability] = useState(null);

  // NEW: Form state (from StaffAvailability)
  const [formData, setFormData] = useState({
    startDateTime: '',
    endDateTime: '',
    comments: '',
    categoryOfStaff: '',
    wardDepartmentArea: '',
    isUnavailable: false,
    unavailabilityReason: '',
    unavailabilityType: 'Full Day',
    unavailabilityDetails: '',
    isSickLeave: false,
    sickLeaveType: 'Full Day',
    sicknessReason: '',
    requestedBy: '',
    requestedByName: '',
    requestedByEmail: '',
    createdByManager: undefined
  });

  // Existing edit form state
  const [editForm, setEditForm] = useState({
    startDateTime: '',
    endDateTime: '',
    wardDepartmentArea: '',
    categoryOfStaff: '',
    reasonForRequest: '',
    specialRequirements: '',
    comments: '',
  });

  // Calculate week dates
  const getWeekDates = (date) => {
    const current = new Date(date);
    current.setHours(0, 0, 0, 0);
    const day = current.getDay();
    const diff = current.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(current.setDate(diff));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    return {
      startDate: monday.toISOString().split('T')[0],
      endDate: sunday.toISOString().split('T')[0],
      fullEndDate: sunday.toISOString(),
      days: Array.from({ length: 7 }, (_, i) => {
        const day = new Date(monday);
        day.setDate(monday.getDate() + i);
        const dayOfWeek = day.getDay();
        return {
          date: day.toISOString().split('T')[0],
          dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek],
          shortName: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek],
          isWeekend: dayOfWeek === 0 || dayOfWeek === 6
        };
      })
    };
  };

  const weekInfo = useMemo(() => getWeekDates(selectedWeek), [selectedWeek]);

  // Parse query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const weekParam = queryParams.get('week');
    if (weekParam) {
      try {
        const date = new Date(weekParam);
        if (!isNaN(date.getTime())) {
          setSelectedWeek(date);
        }
      } catch (error) {
        console.error('Invalid date in URL:', error);
      }
    }
  }, [location]);

  // Fetch data
  const { data: staffData, isLoading: loadingStaff, refetch: refetchStaff } = useGetUsersQuery();
  const { data: shiftsData, isLoading: loadingShifts, refetch: refetchShifts } = useGetWeeklyShiftsByStaffQuery({
    startDate: weekInfo.startDate,
    endDate: weekInfo.fullEndDate
  });
  const { data: availableShiftsData, refetch: refetchAvailable } = useGetAvailableShiftsQuery({
    startDate: weekInfo.startDate,
    endDate: weekInfo.fullEndDate
  });
  const { data: availabilitiesData, refetch: refetchAvailabilities } = useGetWeeklyAvailabilitiesByStaffQuery({
    startDate: weekInfo.startDate,
    endDate: weekInfo.fullEndDate
  });
  const { data: hospitalLocations = [] } = useGetHospitalLocationsQuery();

  // Mutations
  const [assignStaff] = useAssignStaffToShiftMutation();
  const [reOpenShift] = useReOpenShiftMutation();
  const [updateShift] = useUpdateShiftMutation();
  const [createAvailability] = useCreateAvailabilityMutation();
  const [updateAvailability] = useUpdateAvailabilityMutation();
  const [deleteAvailability] = useDeleteAvailabilityMutation();

  // Layout effect
  useEffect(() => {
    setFluid(true);
    return () => {
      setFluid(false);
    };
  }, [setFluid]);

  // Process staff data
  useEffect(() => {
    if (staffData) {
      const filteredStaff = staffData.filter(user => !user.isAdmin && !user.isAgency);
      setStaffList(filteredStaff);
      setFilteredStaffList(filteredStaff);
      
      const jobTitles = [...new Set(filteredStaff
        .map(staff => staff.jobtitle)
        .filter(title => title && title.trim() !== '')
      )].sort();
      setAvailableJobTitles(['all', ...jobTitles]);
    }
  }, [staffData]);

  // Process available shifts data
  useEffect(() => {
    if (availableShiftsData) {
      if (!isEqual(availableShifts, availableShiftsData)) {
        setAvailableShifts(availableShiftsData);
      }
      
      let filteredShifts = availableShiftsData;
      if (selectedWardFilter !== 'all') {
        filteredShifts = filteredShifts.filter(shift => 
          shift.wardDepartmentArea === selectedWardFilter
        );
      }
      
      if (!isEqual(filteredAvailableShifts, filteredShifts)) {
        setFilteredAvailableShifts(filteredShifts);
      }
      
      const wards = [...new Set(availableShiftsData
        .map(shift => shift.wardDepartmentArea)
        .filter(ward => ward && ward.trim() !== '')
      )].sort();
      
      if (!isEqual(availableWards, ['all', ...wards])) {
        setAvailableWards(['all', ...wards]);
      }
    }
  }, [availableShiftsData, selectedWardFilter]);

  // Process staff availabilities
 // Process staff availabilities
useEffect(() => {
  if (availabilitiesData && staffList.length > 0) {
    const availabilitiesByStaff = {};
    staffList.forEach(staff => {
      availabilitiesByStaff[staff._id] = {};
      weekInfo.days.forEach(day => {
        availabilitiesByStaff[staff._id][day.date] = [];
      });
    });
    
    availabilitiesData.forEach(availability => {
      if (!availability.requestedBy) return;
      
      let staffId;
      if (typeof availability.requestedBy === 'object') {
        staffId = availability.requestedBy._id;
      } else {
        staffId = availability.requestedBy;
      }
      
      if (!staffId || !availabilitiesByStaff[staffId]) return;
      
      const availabilityStart = new Date(availability.startDateTime);
      const availabilityEnd = new Date(availability.endDateTime);
      const dateString = availabilityStart.toISOString().split('T')[0];
      
      if (availabilitiesByStaff[staffId][dateString] !== undefined) {
        const dayOfWeek = availabilityStart.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        
        // CRITICAL: Make sure all unavailability fields are properly extracted
        // Check if this is actually an unavailability record
        const isUnavailableRecord = availability.isUnavailable || availability.isSickLeave;
        
        availabilitiesByStaff[staffId][dateString].push({
          id: availability._id,
          startDateTime: availability.startDateTime,
          endDateTime: availability.endDateTime,
          startTime: availabilityStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          endTime: availabilityEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          durationMinutes: availability.durationMinutes,
          comments: availability.comments,
          status: availability.status || 'Pending',
          isWeekend: isWeekend,
          categoryOfStaff: availability.categoryOfStaff || 'General',
          ward: availability.wardDepartmentArea || '',
          location: availability.hospitalLocationId?.name || '',
          
          // UNVAILABILITY FIELDS - Make sure they're properly set
          isSickLeave: availability.isSickLeave || false,
          sickLeaveType: availability.sickLeaveType || 'Full Day',
          sicknessReason: availability.sicknessReason || '',
          
          isUnavailable: availability.isUnavailable || false,
          unavailabilityReason: availability.unavailabilityReason || '',
          unavailabilityType: availability.unavailabilityType || 'Full Day',
          unavailabilityDetails: availability.unavailabilityDetails || '',
          
          // For debugging - add a combined field
          isActuallyUnavailable: isUnavailableRecord,
          
          createdByManager: availability.createdByManager
        });
        
        // Debug log for unavailability records
        if (isUnavailableRecord) {
         console.log('📝 Found unavailability record:', {
            staffId,
            date: dateString,
            isSickLeave: availability.isSickLeave,
            isUnavailable: availability.isUnavailable,
            reason: availability.unavailabilityReason || availability.sicknessReason,
            startTime: availabilityStart.toLocaleTimeString(),
            endTime: availabilityEnd.toLocaleTimeString()
          });
        }
      }
    });
    
   //console.log('Processed availabilities data:', availabilitiesByStaff);
    
    if (!isEqual(staffAvailabilities, availabilitiesByStaff)) {
      setStaffAvailabilities(availabilitiesByStaff);
    }
  }
}, [availabilitiesData, staffList, weekInfo.days]);


  // Apply filters
  useEffect(() => {
    let filteredStaff = [...staffList];
    
    if (selectedJobTitleFilter !== 'all') {
      filteredStaff = filteredStaff.filter(staff => 
        staff.jobtitle === selectedJobTitleFilter
      );
    }
    
    if (!isEqual(filteredStaffList, filteredStaff)) {
      setFilteredStaffList(filteredStaff);
    }
  }, [selectedJobTitleFilter, staffList]);

  // Process staff shifts
  useEffect(() => {
    if (shiftsData && staffList.length > 0) {
      const shiftsByStaff = {};
      staffList.forEach(staff => {
        shiftsByStaff[staff._id] = {};
        weekInfo.days.forEach(day => {
          shiftsByStaff[staff._id][day.date] = [];
        });
      });
      
      shiftsData.forEach(shift => {
        if (shift.filledBy) {
          const staffId = shift.filledBy._id || shift.filledBy;
          const shiftDate = new Date(shift.startDateTime).toISOString().split('T')[0];
          
          if (shiftsByStaff[staffId] && shiftsByStaff[staffId][shiftDate]) {
            const dayOfWeek = new Date(shift.startDateTime).getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
            const isAgency = !!shift.filledByAgency;
            const isCancelled = shift.lineStatus === 'Cancelled';
            
            shiftsByStaff[staffId][shiftDate].push({
              id: shift._id,
              startTime: new Date(shift.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              endTime: new Date(shift.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              ward: shift.wardDepartmentArea,
              location: shift.hospitalLocationId?.name,
              categoryOfStaff: shift.categoryOfStaff,
              isWeekendShift: isWeekend,
              isAgencyFilled: isAgency,
              isCancelled: isCancelled,
              lineStatus: shift.lineStatus,
              shiftData: shift
            });
          }
        }
      });
      
      if (!isEqual(staffShifts, shiftsByStaff)) {
        setStaffShifts(shiftsByStaff);
      }
    }
  }, [shiftsData, staffList, weekInfo.days]);

  const generateSchedulePlainText = (staffId) => {
  const staff = staffList.find(s => s._id === staffId);
  if (!staff) return '';
  
  const emailDays = getEmailDays();
  
  let text = `WEEKLY SCHEDULE\n`;
  text += `===============\n\n`;
  text += `Staff: ${staff.name}\n`;
  text += `Week: ${formatDateRange()}\n`;
  text += `Job Title: ${staff.jobtitle || 'Not specified'}\n`;
  text += `Speciality: ${staff.speciality || 'General'}\n`;
  text += `Generated: ${new Date().toLocaleDateString()}\n\n`;
  text += `='.repeat(60)}\n\n`;
  
  let hasShifts = false;
  
  emailDays.forEach(day => {
    const shifts = staffShifts[staffId]?.[day.date] || [];
    const dayHasShifts = shifts.length > 0;
    
    if (dayHasShifts) hasShifts = true;
    
    text += `${day.dayName.toUpperCase()} (${day.date})`;
    if (day.isWeekend) text += ' [WEEKEND]';
    text += '\n';
    text += '-'.repeat(40) + '\n';
    
    if (dayHasShifts) {
      shifts.forEach((shift, index) => {
        text += `${index + 1}. ${shift.startTime} - ${shift.endTime}\n`;
        text += `   Ward: ${shift.ward}\n`;
        text += `   Location: ${shift.location || 'Not specified'}\n`;
        text += `   Category: ${shift.categoryOfStaff}\n`;
        if (shift.isWeekendShift) text += `   * Weekend Shift *\n`;
        if (shift.isAgencyFilled) text += `   * Agency Staff *\n`;
        text += '\n';
      });
    } else {
      text += `No shifts scheduled\n\n`;
    }
    
    text += '\n';
  });
  
  if (!hasShifts) {
    text += `\nNO SHIFTS SCHEDULED\n`;
    text += `You have no assigned shifts from ${formatDateRange()}\n\n`;
  }
  
  text += `\nIMPORTANT NOTES:\n`;
  text += `- This schedule is subject to change based on operational needs\n`;
  text += `- Please arrive 15 minutes before your scheduled start time\n`;
  text += `- Contact your manager if you have any questions or need to make changes\n`;
  text += `- Weekend shifts are highlighted\n\n`;
  text += `Beaumont Hospital Staff Scheduling System\n`;
  text += `This schedule was automatically generated.\n`;
  text += `For questions, contact: beaumontNursingBank@beaumont.ie\n`;
  
  return text;
};


const openOutlookEmail = (staffId, staffEmail, subject, body) => {
  const mailtoLink = `mailto:${staffEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  
  // Try to open Outlook
  window.location.href = mailtoLink;
  
  // Check if Outlook opened
  setTimeout(() => {
    // If still on the same page after 500ms, Outlook probably didn't open
    if (window.location.href.includes('mailto:')) {
      // Outlook didn't open, fall back to web email
      showSnackbar('Outlook not detected. Using web email instead.', 'info');
      setEmailMethod('web');
      setEmailDialogOpen(true);
    }
  }, 500);
};

// Debug function to check availability data
const debugAvailabilityData = () => {
 //console.log('=== AVAILABILITY DATA DEBUG ===');
  
  filteredStaffList.forEach(staff => {
    const staffAvailabilitiesData = staffAvailabilities[staff._id] || {};
    
    Object.entries(staffAvailabilitiesData).forEach(([date, availabilities]) => {
      availabilities.forEach((availability, idx) => {
        if (availability.isUnavailable || availability.isSickLeave) {
         console.log(`🔴 UNAVAILABLE FOUND:`, {
            staff: staff.name,
            date: date,
            startTime: availability.startTime,
            endTime: availability.endTime,
            isUnavailable: availability.isUnavailable,
            isSickLeave: availability.isSickLeave,
            reason: availability.unavailabilityReason || availability.sicknessReason,
            unavailabilityType: availability.unavailabilityType,
            sickLeaveType: availability.sickLeaveType
          });
        } else {
         console.log(`🟢 AVAILABLE:`, {
            staff: staff.name,
            date: date,
            startTime: availability.startTime,
            endTime: availability.endTime,
            status: availability.status
          });
        }
      });
    });
  });
  
 //console.log('=== END DEBUG ===');
};

const handleSendWebEmail = async () => {
  if (!selectedStaffForEmail) return;
  
  try {
    // Generate HTML and plain text versions
    const htmlContent = generateScheduleHTMLTable(selectedStaffForEmail.id);
    const plainTextContent = generateSchedulePlainText(selectedStaffForEmail.id);
    
    // Construct email data
    const emailData = {
      to: selectedStaffForEmail.email,
      subject: emailSubject,
      text: plainTextContent + '\n\n' + emailContent,
      html: htmlContent + '<hr><p><em>Additional Notes:</em></p><p>' + emailContent.replace(/\n/g, '<br>') + '</p>'
    };
    
    // TODO: Call your email API here
    // Example:
    // await sendEmailApi(emailData).unwrap();
    
    // For demo purposes, we'll just log and show success
   //console.log('Email data:', emailData);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    showSnackbar(`Email sent to ${selectedStaffForEmail.name} via web email`, 'success');
    setEmailDialogOpen(false);
    
  } catch (error) {
    console.error('Failed to send email:', error);
    showSnackbar('Failed to send email', 'error');
  }
};


const generateScheduleHTMLTable = (staffId) => {
  const staff = staffList.find(s => s._id === staffId);
  if (!staff) return '';
  
  const emailDays = getEmailDays();
  
  let html = `
    <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
      <h2 style="color: #2c3e50; border-bottom: 2px solid #3498db; padding-bottom: 10px;">
        Weekly Schedule for ${staff.name}
      </h2>
      
      <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 5px;"><strong>Week:</strong> ${formatDateRange()}</td>
            <td style="padding: 5px;"><strong>Job Title:</strong> ${staff.jobtitle || 'Not specified'}</td>
          </tr>
          <tr>
            <td style="padding: 5px;"><strong>Speciality:</strong> ${staff.speciality || 'General'}</td>
            <td style="padding: 5px;"><strong>Generated:</strong> ${new Date().toLocaleDateString()}</td>
          </tr>
        </table>
      </div>
      
      <div style="overflow-x: auto;">
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd;">
          <thead>
            <tr style="background-color: #3498db; color: white;">
              <th style="padding: 12px; text-align: left; width: 15%;">Day</th>
              <th style="padding: 12px; text-align: left; width: 85%;">Shifts</th>
            </tr>
          </thead>
          <tbody>
  `;
  
  let hasShifts = false;
  
  emailDays.forEach((day, dayIndex) => {
    const shifts = staffShifts[staffId]?.[day.date] || [];
    const dayHasShifts = shifts.length > 0;
    
    if (dayHasShifts) hasShifts = true;
    
    html += `
      <tr style="${dayIndex % 2 === 0 ? 'background-color: #f9f9f9;' : 'background-color: white;'}">
        <td style="padding: 10px; border: 1px solid #ddd; vertical-align: top;">
          <div style="font-weight: bold; color: ${day.isWeekend ? '#e67e22' : '#2c3e50'}">
            ${day.dayName}<br>
            <span style="font-size: 12px; color: #7f8c8d;">${day.date}</span>
          </div>
          ${day.isWeekend ? '<div style="font-size: 11px; color: #e67e22; font-style: italic;">Weekend</div>' : ''}
        </td>
        <td style="padding: 10px; border: 1px solid #ddd;">
    `;
    
    if (dayHasShifts) {
      shifts.forEach((shift, shiftIndex) => {
        const shiftColor = shift.isWeekendShift ? '#e67e22' : '#2ecc71';
        html += `
          <div style="
            background-color: ${shiftColor}10;
            border-left: 4px solid ${shiftColor};
            padding: 8px;
            margin-bottom: 8px;
            border-radius: 4px;
          ">
            <table style="width: 100%;">
              <tr>
                <td style="width: 30%;">
                  <strong style="color: ${shiftColor};">${shift.startTime} - ${shift.endTime}</strong>
                </td>
                <td style="width: 35%;">
                  <strong>Ward:</strong> ${shift.ward}
                </td>
                <td style="width: 35%;">
                  <strong>Category:</strong> ${shift.categoryOfStaff}
                </td>
              </tr>
              <tr>
                <td colspan="3" style="padding-top: 4px;">
                  <strong>Location:</strong> ${shift.location || 'Not specified'}
                  ${shift.isWeekendShift ? '<span style="color: #e67e22; margin-left: 10px;">• Weekend Shift</span>' : ''}
                  ${shift.isAgencyFilled ? '<span style="color: #9c27b0; margin-left: 10px;">• Agency Staff</span>' : ''}
                </td>
              </tr>
            </table>
          </div>
        `;
      });
    } else {
      html += `
        <div style="
          background-color: #f5f5f5;
          padding: 12px;
          text-align: center;
          color: #7f8c8d;
          font-style: italic;
          border-radius: 4px;
        ">
          No shifts scheduled
        </div>
      `;
    }
    
    html += `
        </td>
      </tr>
    `;
  });
  
  if (!hasShifts) {
    html += `
      <tr>
        <td colspan="2" style="padding: 30px; text-align: center; color: #7f8c8d;">
          <div style="font-size: 16px; margin-bottom: 10px;">No shifts scheduled for this week</div>
          <div style="font-size: 14px;">You have no assigned shifts from ${formatDateRange()}</div>
        </td>
      </tr>
    `;
  }
  
  html += `
          </tbody>
        </table>
      </div>
      
      <div style="margin-top: 30px; padding: 15px; background-color: #f8f9fa; border-radius: 5px;">
        <h3 style="color: #2c3e50; margin-top: 0;">Important Notes</h3>
        <ul style="color: #555;">
          <li>This schedule is subject to change based on operational needs</li>
          <li>Please arrive 15 minutes before your scheduled start time</li>
          <li>Contact your manager if you have any questions or need to make changes</li>
          <li>Weekend shifts are highlighted in orange</li>
        </ul>
      </div>
      
      <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #7f8c8d; font-size: 12px;">
        <p>
          <strong>Beaumont Hospital Staff Scheduling System</strong><br>
          This schedule was automatically generated. Please do not reply to this email.<br>
          For questions, contact: beaumontNursingBank@beaumont.ie
        </p>
      </div>
    </div>
  `;
  
  return html;
};

const getEmailDays = () => {
  return weekInfo.days.filter(day => includeWeekend || !day.isWeekend);
};


const handlePresetSelect = (presetKey) => {
  const preset = SHIFT_PRESETS[presetKey];
  if (preset) {
    setEditForm(prev => ({
      ...prev,
      selectedPreset: presetKey,
      startTime: preset.start,
      endTime: preset.end,
      escort: presetKey === 'ESCORT'
    }));
  }
};


const calculateShiftDuration = () => {
  if (!editForm.startDate || !editForm.startTime || !editForm.endDate || !editForm.endTime) {
    return 'Please enter dates and times';
  }
  
  const startDateTime = new Date(`${editForm.startDate}T${editForm.startTime}`);
  let endDateTime = new Date(`${editForm.endDate}T${editForm.endTime}`);
  
  // Handle overnight shifts
  if (editForm.endTime < editForm.startTime && editForm.startDate === editForm.endDate) {
    endDateTime.setDate(endDateTime.getDate() + 1);
  }
  
  if (endDateTime <= startDateTime) {
    return 'Invalid: End time must be after start time';
  }
  
  const durationMs = endDateTime - startDateTime;
  const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
  const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
  
  return `${durationHours} hours ${durationMinutes} minutes`;
};






  // Get shift background color
  const getShiftColor = (shift) => {
    if (shift.isCancelled) return SHIFT_STATUS_COLORS.cancelled;
    if (shift.isAgencyFilled) return SHIFT_STATUS_COLORS.agency;
    if (shift.isWeekendShift) return SHIFT_STATUS_COLORS.weekendAssigned;
    return SHIFT_STATUS_COLORS.assigned;
  };

  // Get availability background color
  const getAvailabilityColor = (status) => {
    switch(status) {
      case 'Approved': return '#e8f5e8';
      case 'Rejected': return '#ffebee';
      default: return '#e3f2fd';
    }
  };

  // Get availability border color
  const getAvailabilityBorderColor = (status) => {
    switch(status) {
      case 'Approved': return '#4caf50';
      case 'Rejected': return '#f44336';
      default: return '#2196f3';
    }
  };

  // NEW: Helper functions from StaffAvailability
  const formatDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // NEW: Handle preset selection
  const handlePresetChange = (presetKey) => {
    setSelectedPreset(presetKey);
    
    if (presetKey && SHIFT_PRESETS[presetKey]) {
      const preset = SHIFT_PRESETS[presetKey];
      
      if (formData.startDateTime) {
        const selectedDate = new Date(formData.startDateTime);
        const [startHours, startMinutes] = preset.start.split(':').map(Number);
        const startDateTime = new Date(selectedDate);
        startDateTime.setHours(startHours, startMinutes, 0, 0);
        
        const [endHours, endMinutes] = preset.end.split(':').map(Number);
        const endDateTime = new Date(selectedDate);
        
        const crossesMidnight = endHours < startHours;
        if (crossesMidnight) {
          endDateTime.setDate(endDateTime.getDate() + 1);
        }
        endDateTime.setHours(endHours, endMinutes, 0, 0);
        
        setFormData(prev => ({
          ...prev,
          startDateTime: startDateTime.toISOString().slice(0, 16),
          endDateTime: endDateTime.toISOString().slice(0, 16)
        }));
      }
    }
  };

  // NEW: Handle start date change
  const handleStartDateChange = (e) => {
    const { value } = e.target;
    setFormData(prev => {
      const newStartDate = new Date(value);
      let newEndDate = new Date(value);
      
      if (!prev.endDateTime || new Date(prev.endDateTime) <= newStartDate) {
        newEndDate.setHours(newEndDate.getHours() + 8);
      } else {
        const originalEndDate = new Date(prev.endDateTime);
        originalEndDate.setFullYear(
          newStartDate.getFullYear(),
          newStartDate.getMonth(),
          newStartDate.getDate()
        );
        newEndDate = originalEndDate;
      }
      
      return {
        ...prev,
        startDateTime: value,
        endDateTime: newEndDate.toISOString().slice(0, 16)
      };
    });
    
    setSelectedPreset('');
  };

  // NEW: Handle form changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startDateTime') {
      handleStartDateChange(e);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // NEW: Handle end date change
  const handleEndDateChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      endDateTime: value
    }));
    setSelectedPreset('');
  };

  // NEW: Open availability dialog for creating
  const handleOpenCreateAvailability = (staff, day) => {
    setSelectedStaffForAvailability(staff);
    setSelectedDayForAvailability(day);
    
    // Set default times
    const today = new Date();
    const selectedDate = new Date(day.date);
    
    // Default start time: 09:00
    const defaultStart = new Date(selectedDate);
    defaultStart.setHours(9, 0, 0, 0);
    
    // Default end time: 17:00 (8 hours later)
    const defaultEnd = new Date(selectedDate);
    defaultEnd.setHours(17, 0, 0, 0);
    
    // If today, adjust times
    const todayLocalDate = getLocalDateString(today.toISOString());
    if (day.date === todayLocalDate && today.getHours() >= 9) {
      defaultStart.setHours(today.getHours(), today.getMinutes(), 0, 0);
      defaultEnd.setHours(today.getHours() + 1, today.getMinutes(), 0, 0);
    }
    
    setFormData({
      startDateTime: formatDateTimeLocal(defaultStart.toISOString()),
      endDateTime: formatDateTimeLocal(defaultEnd.toISOString()),
      comments: '',
      categoryOfStaff: staff.jobtitle || '',
      wardDepartmentArea: staff.ward || '',
      isUnavailable: false,
      unavailabilityReason: '',
      unavailabilityType: 'Full Day',
      unavailabilityDetails: '',
      isSickLeave: false,
      sickLeaveType: 'Full Day',
      sicknessReason: '',
      requestedBy: staff._id,
      requestedByName: staff.name,
      requestedByEmail: staff.email,
      createdByManager: (userInfo.isAdmin || userInfo.isManager) ? {
        id: userInfo._id,
        name: userInfo.name,
        email: userInfo.email
      } : undefined
    });
    
    setEditMode(false);
    setEditingAvailability(null);
    setSelectedPreset('');
    setAvailabilityDialogOpen(true);
  };

  // NEW: Open availability dialog for editing
  const handleOpenEditAvailability = (availability, staff) => {
    setSelectedStaffForAvailability(staff);
    setEditingAvailability(availability);
    setEditMode(true);
    setSelectedPreset('');
    
    setFormData({
      startDateTime: formatDateTimeLocal(availability.startDateTime),
      endDateTime: formatDateTimeLocal(availability.endDateTime),
      comments: availability.comments || '',
      categoryOfStaff: staff.jobtitle || availability.categoryOfStaff || '',
      wardDepartmentArea: staff.ward || availability.wardDepartmentArea || '',
      isUnavailable: availability.isUnavailable || availability.isSickLeave || false,
      unavailabilityReason: availability.unavailabilityReason || availability.sicknessReason || '',
      unavailabilityType: availability.unavailabilityType || availability.sickLeaveType || 'Full Day',
      unavailabilityDetails: availability.unavailabilityDetails || '',
      isSickLeave: availability.isSickLeave || false,
      sickLeaveType: availability.sickLeaveType || 'Full Day',
      sicknessReason: availability.sicknessReason || '',
      requestedBy: staff._id,
      requestedByName: staff.name,
      requestedByEmail: staff.email,
      createdByManager: availability.createdByManager
    });
    
    setAvailabilityDialogOpen(true);
  };

  // NEW: Handle form submission (from StaffAvailability)
  const handleFormSubmit = async () => {
    try {
      // Validate form
      if (!formData.startDateTime || !formData.endDateTime) {
        showSnackbar('Start and end times are required', 'error');
        return;
      }
      
      // Parse dates correctly
      const startTime = new Date(formData.startDateTime);
      const endTime = new Date(formData.endDateTime);
      
      if (endTime <= startTime) {
        showSnackbar('End time must be after start time', 'error');
        return;
      }
      
      // Check duration
      const durationHours = (endTime - startTime) / (1000 * 60 * 60);
      if (durationHours > 24) {
        showSnackbar('Availability cannot exceed 24 hours', 'error');
        return;
      }
      
      // Format for backend
      const formatDateForBackend = (date) => {
        const d = new Date(date);
        return d.toISOString();
      };
      
      const startDateTimeISO = formatDateForBackend(startTime);
      const endDateTimeISO = formatDateForBackend(endTime);
      
      // For managers/admins
      if (userInfo.isAdmin || userInfo.isManager) {
        if (!formData.requestedBy) {
          showSnackbar('Staff information is missing', 'error');
          return;
        }
        
        const submissionData = {
          ...formData,
          startDateTime: startDateTimeISO,
          endDateTime: endDateTimeISO,
          ...(formData.isUnavailable && (userInfo.isAdmin || userInfo.isManager) && {
            status: 'Approved',
            approved: true
          }),
          createdByManager: (userInfo.isAdmin || userInfo.isManager) ? {
            id: userInfo._id,
            name: userInfo.name,
            email: userInfo.email,
            date: new Date().toISOString()
          } : undefined,
          requestedBy: formData.requestedBy,
          requestedByName: formData.requestedByName,
          requestedByEmail: formData.requestedByEmail
        };
        
        if (editMode && editingAvailability) {
          // Update existing availability
          await updateAvailability({
            id: editingAvailability.id,
            ...submissionData
          }).unwrap();
          
          showSnackbar('Availability updated successfully', 'success');
        } else {
          // Create new availability
          await createAvailability(submissionData).unwrap();
          showSnackbar('Availability added successfully', 'success');
        }
      } else {
        // Regular users creating their own availability
        const submissionData = {
          ...formData,
          startDateTime: startDateTimeISO,
          endDateTime: endDateTimeISO,
          requestedBy: userInfo._id,
          requestedByName: userInfo.name,
          requestedByEmail: userInfo.email
        };
        
        if (editMode && editingAvailability) {
          await updateAvailability({
            id: editingAvailability.id,
            ...submissionData
          }).unwrap();
          
          showSnackbar('Availability updated successfully', 'success');
        } else {
          await createAvailability(submissionData).unwrap();
          showSnackbar('Availability added successfully', 'success');
        }
      }
      
      // Close dialog and refresh data
      setAvailabilityDialogOpen(false);
      refetchAvailabilities();
      setSelectedPreset('');
      
    } catch (error) {
      console.error('Form submit error:', error);
      showSnackbar(error?.data?.message || 'Failed to save availability', 'error');
    }
  };

  // NEW: Handle delete availability
  const handleDeleteClick = async (availabilityId, event = null) => {
    if (event) {
      event.stopPropagation();
    }
    
    if (window.confirm('Are you sure you want to remove this availability? This action can be reversed by an administrator.')) {
      try {
        await updateAvailability({
          id: availabilityId,
          isActive: false
        }).unwrap();
        
        showSnackbar('Availability removed successfully', 'success');
        refetchAvailabilities();
        
        if (editMode) {
          setAvailabilityDialogOpen(false);
          setEditMode(false);
          setEditingAvailability(null);
        }
      } catch (error) {
        showSnackbar(error?.data?.message || 'Failed to remove availability', 'error');
      }
    }
  };

  // NEW: Handle delete confirmation dialog
  const handleDeleteConfirmOpen = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!editingAvailability) return;
    
    try {
      await updateAvailability({
        id: editingAvailability.id,
        isActive: false
      }).unwrap();
      
      showSnackbar('Availability removed successfully', 'success');
      refetchAvailabilities();
      
      setDeleteConfirmOpen(false);
      setAvailabilityDialogOpen(false);
      setEditMode(false);
      setEditingAvailability(null);
    } catch (error) {
      showSnackbar(error?.data?.message || 'Failed to remove availability', 'error');
      setDeleteConfirmOpen(false);
    }
  };

  // NEW: Close availability dialog
  const handleCloseAvailabilityDialog = () => {
    setAvailabilityDialogOpen(false);
    setEditMode(false);
    setEditingAvailability(null);
    setSelectedPreset('');
    setSelectedStaffForAvailability(null);
    setSelectedDayForAvailability(null);
  };

  // Render availability card with new functionality
  // Render availability card with new functionality
const renderAvailabilityCard = (availability, idx, staffId, dayDate) => {
  const staff = staffList.find(s => s._id === staffId);
  
  // Check if this is sick leave
  const isSickLeave = availability.isSickLeave || false;
  // Check if this is unavailable (any type)
  const isUnavailable = availability.isUnavailable || isSickLeave;
  
  // Handle UNAVAILABLE cards (including sick leave)
  if (isUnavailable) {
    // Determine if it's partial day
    const isPartial = availability.unavailabilityType === 'Partial Day' || 
                     availability.unavailabilityType === 'Half Day AM' || 
                     availability.unavailabilityType === 'Half Day PM' ||
                     availability.sickLeaveType === 'Partial Day' ||
                     availability.sickLeaveType === 'Half Day AM' ||
                     availability.sickLeaveType === 'Half Day PM';
    
    // Get the reason text
    const reason = isSickLeave 
      ? availability.sicknessReason || 'Sick Leave'
      : availability.unavailabilityReason || 'Unavailable';
    
    // Get the type text
    const typeText = isSickLeave 
      ? availability.sickLeaveType 
      : availability.unavailabilityType;
    
    return (
      <Tooltip 
        key={`unavailable-${availability.id}-${idx}`}
        title={
          <div>
            <div><strong>UNAVAILABLE</strong></div>
            {isSickLeave && <div><strong>Type:</strong> Sick Leave</div>}
            <div><strong>Reason:</strong> {reason}</div>
            <div><strong>Duration:</strong> {typeText}</div>
            {availability.startTime && availability.endTime && (
              <div><strong>Time:</strong> {availability.startTime} - {availability.endTime}</div>
            )}
            {availability.comments && <div><strong>Note:</strong> {availability.comments}</div>}
            <div style={{ marginTop: '5px', fontStyle: 'italic' }}>
              Double-click or Ctrl+Click to edit
            </div>
          </div>
        }
        arrow
        className="no-print"
      >
        <Card 
          sx={{ 
            mb: 0.5, 
            bgcolor: isSickLeave ? 
              (isPartial ? SHIFT_STATUS_COLORS.sickLeavePartial : SHIFT_STATUS_COLORS.sickLeave) :
              '#d32f2f', // Red for general unavailability
            color: 'white',
            border: '2px solid',
            borderColor: isSickLeave ? 
              (isPartial ? '#e65100' : '#d32f2f') : 
              '#b71c1c',
            '&:hover': {
              boxShadow: 3,
              transform: 'translateY(-2px)',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }
          }}
          onDoubleClick={() => handleOpenEditAvailability(availability, staff)}
          onClick={(e) => {
            if (e.ctrlKey || e.metaKey) {
              handleOpenEditAvailability(availability, staff);
            }
          }}
        >
          <CardContent sx={{ p: 1, position: 'relative' }}>
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.2)',
                padding: '2px',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.4)',
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditAvailability(availability, staff);
              }}
              className="no-print"
            >
              <Edit fontSize="small" />
            </IconButton>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                {isSickLeave ? '🏥 SICK LEAVE' : '⛔ UNAVAILABLE'}
              </Typography>
              <Chip 
                label={typeText}
                size="small"
                sx={{ 
                  height: 16,
                  fontSize: '0.6rem',
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  color: 'white'
                }} 
              />
            </Box>
            
            {availability.startTime && availability.endTime && (
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                {availability.startTime} - {availability.endTime}
              </Typography>
            )}
            
            {reason && reason !== 'Unavailable' && (
              <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                {reason}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Tooltip>
    );
  }
  
  // Regular availability card (available, not unavailable)
  return (
    <Tooltip 
      key={`availability-${availability.id}-${idx}`}
      title={
        <div>
          <div><strong>Status:</strong> {availability.status}</div>
          <div><strong>Category:</strong> {availability.categoryOfStaff}</div>
          {availability.ward && <div><strong>Ward:</strong> {availability.ward}</div>}
          {availability.comments && <div><strong>Note:</strong> {availability.comments}</div>}
          <div><strong>Duration:</strong> {formatHoursMinutes(availability.durationMinutes)}</div>
          <div style={{ marginTop: '5px', fontStyle: 'italic' }}>
            Double-click or Ctrl+Click to edit availability
          </div>
        </div>
      }
      arrow
      className="no-print"
    >
      <Card 
        sx={{ 
          mb: 0.5, 
          bgcolor: getAvailabilityColor(availability.status),
          border: '1px dashed',
          borderColor: getAvailabilityBorderColor(availability.status),
          '&:hover': {
            boxShadow: 3,
            transform: 'translateY(-2px)',
            transition: 'all 0.2s',
            cursor: 'pointer'
          }
        }}
        onDoubleClick={() => handleOpenEditAvailability(availability, staff)}
        onClick={(e) => {
          if (e.ctrlKey || e.metaKey) {
            handleOpenEditAvailability(availability, staff);
          }
        }}
        className="availability-card"
      >
        <CardContent sx={{ p: 1, position: 'relative' }}>
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              color: getAvailabilityBorderColor(availability.status),
              backgroundColor: 'rgba(255,255,255,0.7)',
              padding: '2px',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)',
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditAvailability(availability, staff);
            }}
            className="no-print"
          >
            <Edit fontSize="small" />
          </IconButton>
          
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#333', fontWeight: 'bold', pr: 3 }}>
            Available: {availability.startTime} - {availability.endTime}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
            <Chip 
              label={availability.status}
              size="small"
              sx={{ 
                height: 16,
                fontSize: '0.6rem',
                bgcolor: getAvailabilityBorderColor(availability.status),
                color: 'white'
              }}
            />
            <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#666' }}>
              {formatHoursMinutes(availability.durationMinutes)}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Tooltip>
  );
};

  // Existing functions (keep these from your original code)
  const checkStaffAvailabilityForShift = useCallback((staffId, shift) => {
  if (!staffId || !shift || !shift.startDateTime) return false;
  
  const staffAvailabilitiesData = staffAvailabilities[staffId] || {};
  
  // Get staff info for category matching
  const staff = staffList.find(s => s._id === staffId);
  const staffJobTitle = staff?.jobtitle;
  const shiftCategory = shift.categoryOfStaff;
  
  // CATEGORY CHECK - Apply same logic as WardWeeklyView
  if (shiftCategory && staffJobTitle) {
    // SPECIAL HANDLING FOR HCA CATEGORIES
    // Check if both contain "HCA" - allow any order matching
    const shiftHasHCA = shiftCategory.includes('HCA');
    const staffHasHCA = staffJobTitle.includes('HCA');
    
    if (shiftHasHCA && staffHasHCA) {
      // Both have HCA - allow the match regardless of prefix/suffix
     //console.log('✅ HCA match found - category check passed');
    } else {
      // For non-HCA categories, use partial matching or exact matching
      const categoryMatch = staffJobTitle.includes(shiftCategory) || 
                          shiftCategory.includes(staffJobTitle) ||
                          staffJobTitle === shiftCategory;
      
      if (!categoryMatch) {
       console.log('❌ Category mismatch prevents assignment:', {
          shiftCategory,
          staffJobTitle
        });
        return false;
      }
    }
  } else if (shiftCategory && !staffJobTitle) {
    // Shift has category but staff has no job title
   //console.log('❌ Shift requires category but staff has no job title');
    return false;
  }
  
  // Get the shift date and day of week
  const shiftStart = new Date(shift.startDateTime);
  const shiftEnd = new Date(shift.endDateTime);
  const shiftDate = shiftStart.toISOString().split('T')[0];
  const shiftDayOfWeek = shiftStart.getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Check for sick leave on this date
  const sickLeavesForDate = staffAvailabilitiesData[shiftDate]?.filter(
    a => a.isSickLeave
  ) || [];

  // If there's a full day sick leave, staff is not available
  const fullDaySickLeave = sickLeavesForDate.find(
    a => a.sickLeaveType === 'Full Day'
  );
  
  if (fullDaySickLeave) {
   //console.log(`Staff has full day sick leave on ${shiftDate}`);
    return false;
  }
  
  // Check for partial sick leave that overlaps with shift
  const partialSickLeaves = sickLeavesForDate.filter(
    a => a.sickLeaveType === 'Partial Day'
  );
  
  for (const sickLeave of partialSickLeaves) {
    const sickStart = new Date(`${shiftDate}T${sickLeave.startTime}`);
    const sickEnd = new Date(`${shiftDate}T${sickLeave.endTime}`);
    
    // Handle overnight sick leave (though unlikely)
    if (sickLeave.endTime < sickLeave.startTime) {
      sickEnd.setDate(sickEnd.getDate() + 1);
    }
    
    // Check if shift overlaps with sick leave period
    if ((sickStart <= shiftStart && shiftStart < sickEnd) ||
        (sickStart < shiftEnd && shiftEnd <= sickEnd) ||
        (shiftStart <= sickStart && shiftEnd >= sickEnd)) {
     //console.log(`Shift overlaps with partial sick leave: ${sickLeave.startTime}-${sickLeave.endTime}`);
      return false;
    }
  }

  // Convert day of week to string for comparison
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const shiftDayName = dayNames[shiftDayOfWeek];
  
  // Check availabilities for this specific date
  const availabilitiesForDate = staffAvailabilitiesData[shiftDate] || [];
  
  // Check each availability for this staff on the shift date
  for (const availability of availabilitiesForDate) {
    // Skip unavailable periods (sick leave already handled above)
    if (availability.isUnavailable) {
      continue;
    }
    
    // Parse availability times
    const availabilityStart = new Date(`${shiftDate}T${availability.startTime}`);
    const availabilityEnd = new Date(`${shiftDate}T${availability.endTime}`);
    
    // Handle overnight availabilities
    if (availability.endTime < availability.startTime) {
      availabilityEnd.setDate(availabilityEnd.getDate() + 1);
    }
    
    // Check if shift time fits within availability time
    // We need to check if the shift is fully contained within the availability
    if (availabilityStart <= shiftStart && availabilityEnd >= shiftEnd) {
      return true; // Staff has availability that covers the entire shift
    }
    
    // Also check for partial matches (shift starts or ends within availability)
    if ((availabilityStart <= shiftStart && shiftStart < availabilityEnd) ||
        (availabilityStart < shiftEnd && shiftEnd <= availabilityEnd)) {
      return true; // Staff has availability that at least partially covers the shift
    }
  }
  
  // If no availabilities for the exact date, check if staff has any approved availabilities
  // This is a more lenient check - you might want to adjust this based on your requirements
  const allStaffAvailabilities = Object.values(staffAvailabilitiesData).flat();
  const approvedAvailabilities = allStaffAvailabilities.filter(a => 
    a.status === 'Approved' && !a.isUnavailable && !a.isSickLeave
  );
  
  if (approvedAvailabilities.length === 0) {
   //console.log(`Staff ${staffId} has no approved availabilities`);
    return false;
  }
  
 //console.log(`Staff ${staffId} has ${approvedAvailabilities.length} approved availabilities`);
  
  // Return true if staff has any approved availabilities (more lenient approach)
  // For stricter checking, return false here
  return true;
  
}, [staffAvailabilities, staffList]);

  const checkStaffAvailabilityWithDetails = useCallback((staffId, shift) => {
  if (!staffId || !shift || !shift.startDateTime) {
    return { hasAvailability: false, reason: 'Invalid data' };
  }
  
  const staffAvailabilitiesData = staffAvailabilities[staffId] || {};
  const shiftStart = new Date(shift.startDateTime);
  const shiftEnd = new Date(shift.endDateTime);
  const shiftDate = shiftStart.toISOString().split('T')[0];
  
  // Get staff info for category matching
  const staff = staffList.find(s => s._id === staffId);
  const staffJobTitle = staff?.jobtitle;
  const shiftCategory = shift.categoryOfStaff;
  
  // CATEGORY CHECK - Apply same logic as WardWeeklyView
  if (shiftCategory && staffJobTitle) {
    // SPECIAL HANDLING FOR HCA CATEGORIES
    // Check if both contain "HCA" - allow any order matching
    const shiftHasHCA = shiftCategory.includes('HCA');
    const staffHasHCA = staffJobTitle.includes('HCA');
    
   console.log('HCA Category Check:', {
      shiftCategory,
      staffJobTitle,
      shiftHasHCA,
      staffHasHCA
    });
    
    if (shiftHasHCA && staffHasHCA) {
      // Both have HCA - allow the match regardless of prefix/suffix
     //console.log('✅ HCA match found - category check passed');
    } else {
      // For non-HCA categories, use partial matching or exact matching
      const categoryMatch = staffJobTitle.includes(shiftCategory) || 
                          shiftCategory.includes(staffJobTitle) ||
                          staffJobTitle === shiftCategory;
      
     console.log('Non-HCA Category Match Result:', {
        shiftCategory,
        staffJobTitle,
        partialMatch: staffJobTitle.includes(shiftCategory),
        categoryMatch
      });
      
      if (!categoryMatch) {
        return { 
          hasAvailability: false, 
          reason: `Staff (${staffJobTitle}) does not match required category: ${shiftCategory}`,
          categoryMismatch: true
        };
      }
    }
  } else if (shiftCategory && !staffJobTitle) {
    // Shift has category but staff has no job title
    return { 
      hasAvailability: false, 
      reason: `Shift requires category (${shiftCategory}) but staff has no job title`,
      categoryMismatch: true
    };
  }
  
  // FIRST: Check for ANY type of unavailability on this date
  const unavailabilitiesForDate = staffAvailabilitiesData[shiftDate]?.filter(
    a => a.isUnavailable || a.isSickLeave
  ) || [];

  // If there's a full day unavailability of any type, staff is not available
  const fullDayUnavailability = unavailabilitiesForDate.find(
    a => a.unavailabilityType === 'Full Day' || a.sickLeaveType === 'Full Day'
  );
  
  if (fullDayUnavailability) {
    const reasonText = unavailabilitiesForDate.find(a => a.isSickLeave) 
      ? `Staff is on sick leave: ${fullDayUnavailability.sicknessReason || 'No reason specified'}`
      : `Staff is unavailable: ${fullDayUnavailability.unavailabilityReason || 'No reason specified'}`;
    
    return { 
      hasAvailability: false, 
      reason: reasonText,
      isUnavailable: true,
      unavailabilityDetails: fullDayUnavailability
    };
  }
  
  // Check for partial unavailability that overlaps with shift
  const partialUnavailabilities = unavailabilitiesForDate.filter(
    a => a.unavailabilityType !== 'Full Day' && a.sickLeaveType !== 'Full Day'
  );
  
  for (const unavailability of partialUnavailabilities) {
    const unavailStart = new Date(`${shiftDate}T${unavailability.startTime}`);
    const unavailEnd = new Date(`${shiftDate}T${unavailability.endTime}`);
    
    // Handle overnight unavailability
    if (unavailability.endTime < unavailability.startTime) {
      unavailEnd.setDate(unavailEnd.getDate() + 1);
    }
    
    // Check if shift overlaps with unavailability period
    if ((unavailStart <= shiftStart && shiftStart < unavailEnd) ||
        (unavailStart < shiftEnd && shiftEnd <= unavailEnd) ||
        (shiftStart <= unavailStart && shiftEnd >= unavailEnd)) {
      
      const reasonText = unavailability.isSickLeave
        ? `Shift overlaps with sick leave: ${unavailability.sicknessReason || 'No reason specified'}`
        : `Shift overlaps with unavailability: ${unavailability.unavailabilityReason || 'No reason specified'}`;
      
      return { 
        hasAvailability: false, 
        reason: reasonText,
        isUnavailable: true,
        unavailabilityDetails: unavailability
      };
    }
  }

  // SECOND: Check for availabilities for this specific date (only if not unavailable)
  const availabilitiesForDate = staffAvailabilitiesData[shiftDate]?.filter(
    a => !a.isUnavailable && !a.isSickLeave
  ) || [];
  
  // Check each availability for this staff on the shift date
  for (const availability of availabilitiesForDate) {
    // Parse availability times
    const availabilityStart = new Date(`${shiftDate}T${availability.startTime}`);
    const availabilityEnd = new Date(`${shiftDate}T${availability.endTime}`);
    
    // Handle overnight availabilities
    if (availability.endTime < availability.startTime) {
      availabilityEnd.setDate(availabilityEnd.getDate() + 1);
    }
    
    // Check if shift time fits within availability time
    if (availabilityStart <= shiftStart && availabilityEnd >= shiftEnd) {
      return { 
        hasAvailability: true, 
        reason: 'Shift fully covered by availability',
        availabilityStatus: availability.status
      };
    }
    
    // Also check for partial matches
    if ((availabilityStart <= shiftStart && shiftStart < availabilityEnd) ||
        (availabilityStart < shiftEnd && shiftEnd <= availabilityEnd)) {
      return { 
        hasAvailability: true, 
        reason: 'Shift partially covered by availability',
        availabilityStatus: availability.status,
        isPartial: true
      };
    }
  }
  
  // THIRD: Check if staff has any availabilities at all (excluding unavailability)
  const allAvailabilities = Object.values(staffAvailabilitiesData).flat();
  const approvedAvailabilities = allAvailabilities.filter(a => 
    a.status === 'Approved' && !a.isUnavailable && !a.isSickLeave
  );
  
  if (approvedAvailabilities.length === 0) {
    return { 
      hasAvailability: false, 
      reason: 'No approved availabilities',
      totalAvailabilities: allAvailabilities.length
    };
  }
  
  // If we get here, staff has some approved availabilities but not for this specific time
  return { 
    hasAvailability: true,
    reason: 'Staff has approved availabilities but not for this specific time',
    isPartial: false
  };
}, [staffAvailabilities, staffList]);

  const handleDragStart = (e, shift) => {
    setDraggedItem(shift);
    e.dataTransfer.setData('text/plain', JSON.stringify(shift));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, staffId, dayDate) => {
  e.preventDefault();
  
  if (!draggedItem) return;
  
  // Check if staff has availability for this shift with detailed feedback
  const availabilityCheck = checkStaffAvailabilityWithDetails(staffId, draggedItem);
  
   if (!availabilityCheck.hasAvailability) {
  const staffName = staffList.find(s => s._id === staffId)?.name || 'Staff member';
  const shiftDate = new Date(draggedItem.startDateTime);
  const shiftTime = `${shiftDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(draggedItem.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

  // Show specific error message for unavailability
  if (availabilityCheck.isUnavailable) {
    showSnackbar(
      `⛔ ${staffName} is unavailable: ${availabilityCheck.reason} (${shiftTime})`,
      'error'
    );
  } else {
    showSnackbar(
      `❌ ${staffName} cannot be assigned: ${availabilityCheck.reason} (${shiftTime})`,
      'error'
    );
  }
  return;
}
  
  // Optionally, you can still show a warning for partial coverage
  if (availabilityCheck.isPartial) {
    const staffName = staffList.find(s => s._id === staffId)?.name || 'Staff member';
    showSnackbar(
      `Note: ${staffName}'s availability only partially covers this shift`,
      'warning'
    );
  }
  
  try {
    // Assign staff to shift
    await assignStaff({
      shiftId: draggedItem._id,
      staffData: {
        userId: staffId,
        userName: staffList.find(s => s._id === staffId)?.name,
        userEmail: staffList.find(s => s._id === staffId)?.email,
        managerId: userInfo._id
      }
    }).unwrap();
    
    // Update local state
    const updatedShifts = { ...staffShifts };
    if (!updatedShifts[staffId]) {
      updatedShifts[staffId] = {};
    }
    if (!updatedShifts[staffId][dayDate]) {
      updatedShifts[staffId][dayDate] = [];
    }
    
    const dayOfWeek = new Date(dayDate).getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Saturday (6) or Sunday (0)
    const isAgency = !!draggedItem.filledByAgency;
    
    updatedShifts[staffId][dayDate].push({
      id: draggedItem._id,
      startTime: new Date(draggedItem.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      endTime: new Date(draggedItem.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      ward: draggedItem.wardDepartmentArea,
      location: draggedItem.hospitalLocationId?.name,
      categoryOfStaff: draggedItem.categoryOfStaff,
      isWeekendShift: isWeekend,
      isAgencyFilled: isAgency,
      isCancelled: false,
      lineStatus: 'FilledByStaff',
      shiftData: draggedItem
    });
    
    setStaffShifts(updatedShifts);
    setDraggedItem(null);
    
    // Remove from available shifts
    const updatedAvailable = availableShifts.filter(shift => shift._id !== draggedItem._id);
    setAvailableShifts(updatedAvailable);
    
    // Update filtered available shifts
    const updatedFilteredAvailable = updatedAvailable.filter(shift => 
      selectedWardFilter === 'all' || shift.wardDepartmentArea === selectedWardFilter
    );
    setFilteredAvailableShifts(updatedFilteredAvailable);
    
    // Refetch data to ensure consistency
    refetchShifts();
    refetchAvailable();
    
    showSnackbar('Shift assigned successfully!', 'success');
    
  } catch (error) {
    console.error('Failed to assign shift:', error);
    const errorMessage = error?.data?.message || error?.message || 'Failed to assign shift';
    showSnackbar(errorMessage, 'error');
  }
};

  const handleShiftDoubleClick = (shift, staffId, dayDate) => {
    if (!shift || !shift.id) return;
    
    const staff = staffList.find(s => s._id === staffId);
    const shiftDate = new Date(shift.shiftData?.startDateTime || dayDate);
    
    setShiftToRelease({
      shiftId: shift.id,
      staffId,
      staffName: staff?.name || 'Unknown Staff',
      shiftDate: shiftDate.toLocaleDateString(),
      shiftTime: `${shift.startTime} - ${shift.endTime}`,
      ward: shift.ward,
      dayDate // Store the date for local state update
    });
    
    setReleaseDialogOpen(true);
  };

  const handleEditShiftClick = (shift, staffId, dayDate) => {
  if (!shift || !shift.shiftData) return;
  
  const shiftData = shift.shiftData;
  const startDateTime = new Date(shiftData.startDateTime);
  const endDateTime = new Date(shiftData.endDateTime);
  
  // Determine which preset matches the shift times
  const startTime = startDateTime.toTimeString().slice(0, 5);
  const endTime = endDateTime.toTimeString().slice(0, 5);
  
  let selectedPreset = '';
  Object.entries(SHIFT_PRESETS).forEach(([key, preset]) => {
    if (preset.start === startTime && preset.end === endTime) {
      selectedPreset = key;
    }
  });
  
  setShiftToEdit({
    shiftId: shift.id,
    staffId,
    staffName: staffList.find(s => s._id === staffId)?.name || 'Unknown',
    dayDate
  });
  
  // Pre-fill the edit form with all fields
   // Pre-fill the edit form with all fields
  setEditForm({
    startDate: startDateTime.toISOString().split('T')[0],
    startTime: startTime,
    endDate: endDateTime.toISOString().split('T')[0],
    endTime: endTime,
    selectedPreset: selectedPreset,
    hospitalLocationId: shiftData.hospitalLocationId?._id || shiftData.hospitalLocationId || '',
    wardDepartmentArea: shiftData.wardDepartmentArea || '',
    categoryOfStaff: shiftData.categoryOfStaff || '',
    reasonForRequest: shiftData.reasonForRequest || '',
    reasonForRequestDetails: shiftData.reasonForRequestDetails || '',
    escort: shiftData.escort || false,
    special: shiftData.special || false,
    isOvertime: shiftData.isOvertime || false,
    isActive: shiftData.isActive !== false,
    eccNumber: shiftData.eccNumber || '',
    specialRequirements: shiftData.specialRequirements || '',
    comments: shiftData.comments || '',
  });
  
  setActiveTab('basic');
  setEditDialogOpen(true);
};

  const handleReleaseShift = async () => {
    if (!shiftToRelease) return;
    
    try {
      // Call reOpenShift mutation
      await reOpenShift({
        shiftId: shiftToRelease.shiftId,
        userId: shiftToRelease.staffId,
        userName: shiftToRelease.staffName,
        userEmail: '', // You might want to get this from staff data
        managerId: userInfo._id
      }).unwrap();
      
      // Update local state - remove shift from staff
      const updatedShifts = { ...staffShifts };
      if (updatedShifts[shiftToRelease.staffId] && updatedShifts[shiftToRelease.staffId][shiftToRelease.dayDate]) {
        updatedShifts[shiftToRelease.staffId][shiftToRelease.dayDate] = 
          updatedShifts[shiftToRelease.staffId][shiftToRelease.dayDate].filter(s => s.id !== shiftToRelease.shiftId);
        setStaffShifts(updatedShifts);
      }
      
      // Add shift back to available shifts (if it's still in the current week)
      const shiftDate = new Date(shiftToRelease.dayDate);
      const weekStart = new Date(weekInfo.startDate);
      const weekEnd = new Date(weekInfo.endDate);
      
      if (shiftDate >= weekStart && shiftDate <= weekEnd) {
        refetchAvailable();
      }
      
      // Refetch all data
      refetchShifts();
      
      showSnackbar('Shift released successfully!', 'success');
      
    } catch (error) {
      console.error('Failed to release shift:', error);
      showSnackbar('Failed to release shift', 'error');
    } finally {
      setReleaseDialogOpen(false);
      setShiftToRelease(null);
    }
  };

  const handleUpdateShift = async () => {
  if (!shiftToEdit || !shiftToEdit.shiftId) return;
  
  try {
    // Combine date and time into ISO strings
    const combineDateTime = (dateStr, timeStr) => {
      const date = new Date(`${dateStr}T${timeStr}`);
      
      // Handle overnight shifts
      if (editForm.endTime < editForm.startTime && editForm.startDate === editForm.endDate) {
        date.setDate(date.getDate() + 1);
      }
      
      return date.toISOString();
    };
    
    // Format dates for backend
    const formattedData = {
      shiftId: shiftToEdit.shiftId,
      startDateTime: combineDateTime(editForm.startDate, editForm.startTime),
      endDateTime: combineDateTime(editForm.endDate, editForm.endTime),
      hospitalLocationId: editForm.hospitalLocationId,
      wardDepartmentArea: editForm.wardDepartmentArea,
      reasonForRequest: editForm.reasonForRequest,
      reasonForRequestDetails: editForm.reasonForRequestDetails,
      categoryOfStaff: editForm.categoryOfStaff,
      escort: editForm.escort,
      special: editForm.special,
      isOvertime: editForm.isOvertime,
      isActive: editForm.isActive,
      eccNumber: editForm.eccNumber,
      specialRequirements: editForm.specialRequirements,
      comments: editForm.comments,
    };
    
    await updateShift(formattedData).unwrap();
    
    // Update local state
    const updatedShifts = { ...staffShifts };
    if (updatedShifts[shiftToEdit.staffId] && updatedShifts[shiftToEdit.staffId][shiftToEdit.dayDate]) {
      const shiftIndex = updatedShifts[shiftToEdit.staffId][shiftToEdit.dayDate].findIndex(
        s => s.id === shiftToEdit.shiftId
      );
      
      if (shiftIndex !== -1) {
        const updatedShift = {
          ...updatedShifts[shiftToEdit.staffId][shiftToEdit.dayDate][shiftIndex],
          startTime: new Date(formattedData.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          endTime: new Date(formattedData.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          ward: editForm.wardDepartmentArea,
          categoryOfStaff: editForm.categoryOfStaff,
        };
        
        updatedShifts[shiftToEdit.staffId][shiftToEdit.dayDate][shiftIndex] = updatedShift;
        setStaffShifts(updatedShifts);
      }
    }
    
    // Refetch data
    refetchShifts();
    
    showSnackbar('Shift updated successfully!', 'success');
    setEditDialogOpen(false);
    setShiftToEdit(null);
    
  } catch (error) {
    console.error('Failed to update shift:', error);
    showSnackbar('Failed to update shift', 'error');
  }
};

  const handleCancelRelease = () => {
    setReleaseDialogOpen(false);
    setShiftToRelease(null);
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setShiftToEdit(null);
  };

  const handleCancelEmail = () => {
    setEmailDialogOpen(false);
    setSelectedStaffForEmail(null);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNavigateToCalendarView = () => {
    navigate('/admin/MonthlyCalendarView');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedWeek);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setSelectedWeek(newDate);
  };

  // NEW: Handle date picker change
  const handleDateChange = (event) => {
    const newDate = new Date(event.target.value);
    if (!isNaN(newDate.getTime())) {
      setSelectedWeek(newDate);
    }
  };

  const goToCurrentWeek = () => {
    setSelectedWeek(new Date());
  };

  const formatDateRange = () => {
    const start = new Date(weekInfo.startDate);
    const end = new Date(weekInfo.endDate);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const handleClearFilters = () => {
    setSelectedWardFilter('all');
    setSelectedJobTitleFilter('all');
    showSnackbar('Filters cleared', 'info');
  };

  const getFilterSummary = () => {
    const filters = [];
    if (selectedWardFilter !== 'all') filters.push(`Ward: ${selectedWardFilter}`);
    if (selectedJobTitleFilter !== 'all') filters.push(`Job: ${selectedJobTitleFilter}`);
    return filters.length > 0 ? filters.join(' • ') : 'No filters applied';
  };

  // Print function
   // PRINT FUNCTION
// PRINT FUNCTION
// PRINT FUNCTION
// PRINT FUNCTION
const handlePrint = () => {

   // Debug first
  debugAvailabilityData();
  
  // Create a printable version of the staff weekly schedule
  const printWindow = window.open('', '_blank');
  
  if (!printWindow) {
    showSnackbar('Please allow pop-ups to print', 'warning');
    return;
  }
  
  // Get the current filter info
  const filterInfo = getFilterSummary();
  
  // Debug: Check what data we have
 //console.log('📊 PRINT DEBUG - Staff availabilities data:', staffAvailabilities);
 //console.log('📊 PRINT DEBUG - Filtered staff list:', filteredStaffList.length);
  
  // Helper function to check if availability is actually unavailable
  const isAvailabilityUnavailable = (availability) => {
    const result = availability.isUnavailable || availability.isSickLeave || availability.isActuallyUnavailable;
   console.log('🔍 Checking unavailability for availability:', {
      id: availability.id,
      isUnavailable: availability.isUnavailable,
      isSickLeave: availability.isSickLeave,
      isActuallyUnavailable: availability.isActuallyUnavailable,
      result: result,
      reason: availability.unavailabilityReason || availability.sicknessReason
    });
    return result;
  };
  
  // Build the printable HTML
  const printHTML = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Staff Weekly Schedule - ${formatDateRange()}</title>
      <style>
        @page {
          size: landscape;
          margin: 0.5in;
        }
        
        body {
          font-family: Arial, sans-serif;
          margin: 0;
          padding: 20px;
          background: white;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
          font-size: 10px;
        }
        
        .print-header {
          text-align: center;
          margin-bottom: 20px;
          border-bottom: 2px solid #000;
          padding-bottom: 10px;
        }
        
        .print-title {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        
        .print-subtitle {
          font-size: 16px;
          color: #666;
          margin-bottom: 10px;
        }
        
        .print-info {
          font-size: 12px;
          color: #666;
          margin-bottom: 5px;
        }
        
        .print-calendar {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          margin-top: 20px;
        }
        
        .print-calendar th {
          background-color: #1976d2;
          color: white;
          padding: 10px;
          text-align: center;
          border: 1px solid #ddd;
          font-weight: bold;
        }
        
        .print-calendar td {
          padding: 5px;
          border: 1px solid #ddd;
          vertical-align: top;
          min-height: 150px;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        
        .staff-header {
          background-color: #1976d2;
          color: white;
          padding: 8px;
          text-align: left;
          font-weight: bold;
          width: 200px;
        }
        
        .day-header {
          background-color: #1976d2;
          color: white;
          padding: 8px;
          text-align: center;
          font-weight: bold;
        }
        
        .weekend-header {
          background-color: #ff9800 !important;
        }
        
        /* UNAVAILABILITY CARDS - PROMINENT STYLING */
        .unavailable-card {
          padding: 6px;
          margin: 3px 0;
          border-radius: 6px;
          font-size: 9px;
          color: white !important;
          font-weight: bold;
          page-break-inside: avoid;
          break-inside: avoid;
          min-height: 50px;
          border: 3px solid rgba(0,0,0,0.3) !important;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .unavailable-sick-full {
          background-color: #f44336 !important;
          border-color: #d32f2f !important;
        }
        
        .unavailable-sick-partial {
          background-color: #ff9800 !important;
          border-color: #e65100 !important;
        }
        
        .unavailable-other {
          background-color: #d32f2f !important;
          border-color: #b71c1c !important;
        }
        
        .unavailable-force-majeure {
          background-color: #9c27b0 !important;
          border-color: #7b1fa2 !important;
        }
        
        /* Regular Availability Cards */
        .availability-card {
          padding: 4px;
          margin: 2px 0;
          border-radius: 4px;
          font-size: 9px;
          color: white !important;
          font-weight: bold;
          page-break-inside: avoid;
          break-inside: avoid;
          min-height: 40px;
          border: 1px dashed rgba(255,255,255,0.5);
        }
        
        .availability-pending {
          background-color: #2196f3 !important;
        }
        
        .availability-approved {
          background-color: #4caf50 !important;
        }
        
        .availability-rejected {
          background-color: #f44336 !important;
        }
        
        /* Shift Cards */
        .shift-card {
          padding: 4px;
          margin: 2px 0;
          border-radius: 4px;
          font-size: 9px;
          color: white !important;
          font-weight: bold;
          page-break-inside: avoid;
          break-inside: avoid;
          min-height: 40px;
          border: 1px solid rgba(0,0,0,0.1);
        }
        
        .shift-assigned {
          background-color: #4caf50 !important;
        }
        
        .shift-weekend-assigned {
          background-color: #ff9800 !important;
        }
        
        .shift-agency {
          background-color: #9c27b0 !important;
        }
        
        .shift-cancelled {
          background-color: #f44336 !important;
        }
        
        .shift-time {
          font-weight: bold;
          font-size: 8px;
          margin-bottom: 2px;
        }
        
        .unavailable-label {
          font-size: 8px;
          font-weight: bold;
          margin-bottom: 2px;
          text-transform: uppercase;
        }
        
        .unavailable-reason {
          font-size: 7px;
          margin-top: 2px;
          font-style: italic;
          opacity: 0.9;
        }
        
        .availability-label {
          font-size: 8px;
          margin-bottom: 2px;
        }
        
        .availability-details {
          font-size: 7px;
          margin-top: 2px;
          opacity: 0.9;
        }
        
        .staff-info {
          padding: 8px;
          background-color: #f5f5f5;
          border-right: 1px solid #ddd;
          min-width: 180px;
        }
        
        .staff-name {
          font-weight: bold;
          font-size: 11px;
        }
        
        .staff-details {
          font-size: 9px;
          color: #666;
          margin-top: 2px;
        }
        
        .staff-job {
          font-size: 9px;
          color: #1976d2;
          font-weight: bold;
        }
        
        .empty-day {
          text-align: center;
          color: #999;
          font-style: italic;
          font-size: 9px;
          padding: 20px 5px;
        }
        
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-header">
        <div class="print-title">Staff Weekly Schedule</div>
        <div class="print-subtitle">Week of ${formatDateRange()}</div>
        <div class="print-info">Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
        <div class="print-info">Generated By: ${userInfo?.name || 'Manager'}</div>
        ${filterInfo !== 'No filters applied' ? `<div class="print-info">Filters: ${filterInfo}</div>` : ''}
        <div class="print-info">Showing ${filteredStaffList.length} staff members</div>
      </div>
      
      <table class="print-calendar">
        <thead>
          <tr>
            <th class="staff-header" style="width: 180px;">Staff Information</th>
            ${weekInfo.days.map(day => `
              <th class="day-header ${day.isWeekend ? 'weekend-header' : ''}" style="width: calc((100% - 180px) / 7);">
                ${day.dayName}<br>
                <span style="font-size: 11px; font-weight: normal;">${day.date}</span>
                ${day.isWeekend ? '<div style="font-size: 10px; font-style: italic;">Weekend</div>' : ''}
              </th>
            `).join('')}
          </tr>
        </thead>
        <tbody>
          ${filteredStaffList.map((staff, index) => {
            const staffShiftsData = staffShifts[staff._id] || {};
            const staffAvailabilitiesData = staffAvailabilities[staff._id] || {};
            
            return `
              <tr>
                <td class="staff-info" style="background-color: ${index % 2 === 0 ? '#f9f9f9' : 'white'};">
                  <div class="staff-name">${staff.name}</div>
                  <div class="staff-job">${staff.jobtitle || 'No title'}</div>
                  <div class="staff-details">${staff.email || ''}</div>
                </td>
                ${weekInfo.days.map(day => {
                  const shifts = staffShiftsData[day.date] || [];
                  const availabilities = staffAvailabilitiesData[day.date] || [];
                  
                  return `
                    <td style="background-color: ${day.isWeekend ? 'rgba(255, 152, 0, 0.05)' : (index % 2 === 0 ? '#f9f9f9' : 'white')};">
                      <!-- Debug info in comments -->
                      <!-- ${day.date}: ${availabilities.length} availability records -->
                      
                      ${shifts.map(shift => {
                        let shiftClass = 'shift-card ';
                        if (shift.isCancelled) shiftClass += 'shift-cancelled';
                        else if (shift.isAgencyFilled) shiftClass += 'shift-agency';
                        else if (shift.isWeekendShift) shiftClass += 'shift-weekend-assigned';
                        else shiftClass += 'shift-assigned';
                        
                        return `
                          <div class="${shiftClass}">
                            <div class="shift-time">${shift.startTime} - ${shift.endTime}</div>
                            <div class="availability-label">${shift.ward}</div>
                            <div class="availability-details">${shift.categoryOfStaff}</div>
                          </div>
                        `;
                      }).join('')}
                      
                      ${availabilities.map((availability, idx) => {
                        // Check if this is actually an unavailability record
                        const isUnavailableRecord = isAvailabilityUnavailable(availability);
                        
                        if (isUnavailableRecord) {
                          // IT'S UNAVAILABLE - Show as UNAVAILABLE, not Available
                          let unavailableClass = 'unavailable-card ';
                          const reason = availability.unavailabilityReason || availability.sicknessReason || 'Unavailable';
                          
                          // Determine the specific class based on reason
                          if (availability.isSickLeave) {
                            const isPartial = availability.sickLeaveType === 'Partial Day' || 
                                             availability.sickLeaveType === 'Half Day AM' || 
                                             availability.sickLeaveType === 'Half Day PM';
                            unavailableClass += isPartial ? 'unavailable-sick-partial' : 'unavailable-sick-full';
                          } else if (reason.toLowerCase().includes('force majeure')) {
                            unavailableClass += 'unavailable-force-majeure';
                          } else {
                            unavailableClass += 'unavailable-other';
                          }
                          
                          const type = availability.unavailabilityType || availability.sickLeaveType || 'Full Day';
                          
                          return `
                            <div class="${unavailableClass}">
                              <div class="shift-time">${availability.startTime} - ${availability.endTime}</div>
                              <div class="unavailable-label">⛔ UNAVAILABLE</div>
                              <div class="unavailable-reason">${type}</div>
                              <div class="unavailable-reason">${reason}</div>
                              ${availability.comments ? `<div class="unavailable-reason">${availability.comments}</div>` : ''}
                            </div>
                          `;
                        } else {
                          // IT'S ACTUALLY AVAILABLE - Show as Available
                          let availabilityClass = 'availability-card ';
                          if (availability.status === 'Approved') availabilityClass += 'availability-approved';
                          else if (availability.status === 'Rejected') availabilityClass += 'availability-rejected';
                          else availabilityClass += 'availability-pending';
                          
                          return `
                            <div class="${availabilityClass}">
                              <div class="shift-time">${availability.startTime} - ${availability.endTime}</div>
                              <div class="availability-label">Available</div>
                              <div class="availability-details">Status: ${availability.status}</div>
                              <div class="availability-details">Duration: ${formatHoursMinutes(availability.durationMinutes)}</div>
                            </div>
                          `;
                        }
                      }).join('')}
                      
                      ${shifts.length === 0 && availabilities.length === 0 ? `
                        <div class="empty-day">
                          No shifts or availability
                        </div>
                      ` : ''}
                    </td>
                  `;
                }).join('')}
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
      
      <div style="margin-top: 30px; padding: 15px; border-top: 2px solid #000; font-size: 10px; color: #666; text-align: center;">
        Beaumont Hospital Staff Scheduling System<br>
        Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}<br>
        For questions, contact: beaumontNursingBank@beaumont.ie
      </div>
      
      <script>
        window.onload = function() {
          // Print after a short delay
          setTimeout(() => {
            window.print();
            setTimeout(() => {
              window.close();
            }, 100);
          }, 500);
        };
      </script>
    </body>
    </html>
  `;
  
  printWindow.document.write(printHTML);
  printWindow.document.close();
};



  // Email functions
	const handleEmailButtonClick = (staffId) => {
    const staff = staffList.find(s => s._id === staffId);
    if (!staff) return;
    
    setSelectedStaffForEmail({
      id: staffId,
      name: staff.name,
      email: staff.email
    });
    
    // Generate email content
    const defaultSubject = `Your Weekly Schedule - Week of ${formatDateRange()}`;
    const plainTextBody = generateSchedulePlainText(staffId);
    
    setEmailSubject(defaultSubject);
    setEmailContent(plainTextBody);
    
    // Try to open Outlook first
    if (emailMethod === 'outlook' && staff.email) {
      try {
        openOutlookEmail(staffId, staff.email, defaultSubject, plainTextBody);
        
        // Show a message that we're trying Outlook
        showSnackbar('Opening Outlook...', 'info');
        
        // Don't open the dialog if Outlook is being used
        return;
      } catch (error) {
        console.error('Failed to open Outlook:', error);
        showSnackbar('Could not open Outlook. Using web email.', 'warning');
        setEmailMethod('web');
      }
    }
    
    // If Outlook failed or web method is selected, open the dialog
    setEmailDialogOpen(true);
  };

  const handleSendEmail = () => {
    if (emailMethod === 'outlook') {
      // Already handled in handleEmailButtonClick
      setEmailDialogOpen(false);
    } else {
      handleSendWebEmail();
    }
  };

  if (loadingStaff || loadingShifts) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PrintStyles />
      
      {/* Navigation Breadcrumbs */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">Staff Weekly View</Typography>
      </Breadcrumbs>
      
      {/* Header with title and buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          Staff Weekly Schedule
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{
              bgcolor: '#673ab7',
              '&:hover': {
                bgcolor: '#5e35b1'
              }
            }}
            className="no-print"
          >
            Print Schedule
          </Button>
          
          <Button
            variant="contained"
            startIcon={<CalendarMonth />}
            onClick={handleNavigateToCalendarView}
            sx={{
              bgcolor: '#1976d2',
              '&:hover': {
                bgcolor: '#1565c0'
              }
            }}
            className="no-print"
          >
            View Calendar Analytics
          </Button>
        </Box>
      </Box>
      
      {/* Top Row: Week Navigation, Date Picker, Filters, and Legend */}
      <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
        {/* Current Week Selector and Date Picker */}
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigateWeek(-1)}>
                  <ArrowBackIos />
                </IconButton>
                <Typography variant="h6">
                  Week of {formatDateRange()}
                </Typography>
                <IconButton onClick={() => navigateWeek(1)}>
                  <ArrowForwardIos />
                </IconButton>
              </Box>
            </Box>
            
            <Button 
              startIcon={<Today />} 
              onClick={goToCurrentWeek}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
            >
              Current Week
            </Button>
            
            {/* NEW: Date Picker Control */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarMonth color="action" />
              <TextField
                type="date"
                value={selectedWeek.toISOString().split('T')[0]}
                onChange={handleDateChange}
                size="small"
                label="Select Date"
                InputLabelProps={{ shrink: true }}
                sx={{ width: '100%' }}
              />
            </Box>
            <Tooltip title="Select any date to view its week">
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                (shows week containing selected date)
              </Typography>
            </Tooltip>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <Chip 
                label={`${filteredStaffList.length} Staff`}
                color="primary"
                variant="outlined"
                size="small"
              />
              <Chip 
                label={`${filteredAvailableShifts.length} Available Shifts`}
                color="secondary"
                variant="outlined"
                size="small"
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Filters */}
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterAlt color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Filters
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  size="small" 
                  onClick={handleClearFilters}
                  disabled={selectedWardFilter === 'all' && selectedJobTitleFilter === 'all'}
                >
                  Clear All
                </Button>
              </Box>
            </Box>
            
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Filter by Job Title</InputLabel>
              <Select
                value={selectedJobTitleFilter}
                label="Filter by Job Title"
                onChange={(e) => setSelectedJobTitleFilter(e.target.value)}
                startAdornment={<FilterList fontSize="small" sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">
                  <em>All Job Titles</em>
                </MenuItem>
                {availableJobTitles.slice(1).map((title) => (
                  <MenuItem key={title} value={title}>
                    {title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small">
              <InputLabel>Filter Available Shifts by Ward</InputLabel>
              <Select
                value={selectedWardFilter}
                label="Filter Available Shifts by Ward"
                onChange={(e) => setSelectedWardFilter(e.target.value)}
                startAdornment={<FilterList fontSize="small" sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">
                  <em>All Wards</em>
                </MenuItem>
                {availableWards.slice(1).map((ward) => (
                  <MenuItem key={ward} value={ward}>
                    {ward}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Box sx={{ mt: 2, p: 1, bgcolor: 'white', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Active Filters:</strong> {getFilterSummary()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Showing:</strong> {filteredStaffList.length} staff
              </Typography>
            </Box>
          </Paper>
        </Grid>
        
        {/* Legend */}
        <Grid item xs={4}>
          <Paper elevation={1} sx={{ p: 2, height: '100%', bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Shift Status Colors:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, bgcolor: SHIFT_STATUS_COLORS.assigned, borderRadius: 1 }} />
                <Typography variant="body2">Assigned to Staff</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, bgcolor: SHIFT_STATUS_COLORS.weekendAssigned, borderRadius: 1 }} />
                <Typography variant="body2">Weekend Assigned</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, bgcolor: SHIFT_STATUS_COLORS.agency, borderRadius: 1 }} />
                <Typography variant="body2">Agency Filled</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, bgcolor: SHIFT_STATUS_COLORS.cancelled, borderRadius: 1 }} />
                <Typography variant="body2">Cancelled</Typography>
              </Box>
            </Box>
            
            <Typography variant="subtitle2" gutterBottom>
              Availability Status:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 20, 
                  height: 20, 
                  bgcolor: '#e3f2fd',
                  border: '1px dashed #2196f3',
                  borderRadius: 1 
                }} />
                <Typography variant="body2">Pending Availability</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 20, 
                  height: 20, 
                  bgcolor: '#e8f5e8',
                  border: '1px dashed #4caf50',
                  borderRadius: 1 
                }} />
                <Typography variant="body2">Approved Availability</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ 
                  width: 20, 
                  height: 20, 
                  bgcolor: '#ffebee',
                  border: '1px dashed #f44336',
                  borderRadius: 1 
                }} />
                <Typography variant="body2">Rejected Availability</Typography>
              </Box>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Sick Leave Status:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, bgcolor: SHIFT_STATUS_COLORS.sickLeave, borderRadius: 1, border: '1px solid #d32f2f' }} />
                <Typography variant="body2">Full Day Sick Leave</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, bgcolor: SHIFT_STATUS_COLORS.sickLeavePartial, borderRadius: 1, border: '1px solid #e65100' }} />
                <Typography variant="body2">Partial Day Sick Leave</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Grid container spacing={3}>
        {/* Main Schedule Table */}
       {/* Main Schedule Table */}
<Grid item xs={9}>
  <Paper elevation={3} sx={{ overflow: 'hidden' }} className="print-page">
    <Box sx={{ minWidth: '1400px' }}>
      {/* Fixed Header Row - OUTSIDE scrollable container */}
      <Grid container sx={fixedHeaderStyle}>
        <Grid item xs={1.7}>
          <Box sx={{ 
            p: 2, 
            borderRight: 1, 
            borderBottom: 1, 
            borderColor: 'divider',
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box>
              <Typography fontWeight="bold">Staff</Typography>
              {selectedJobTitleFilter !== 'all' && (
                <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                  Filtered: {selectedJobTitleFilter}
                </Typography>
              )}
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Actions
            </Typography>
          </Box>
        </Grid>
        {weekInfo.days.map(day => (
          <Grid item xs={1.47} key={day.date}>
            <Box sx={{ 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider',
              bgcolor: day.isWeekend ? 'secondary.main' : 'primary.light',
              color: 'white',
              textAlign: 'center'
            }}>
              <Typography fontWeight="bold">{day.shortName}</Typography>
              <Typography variant="body2">
                {new Date(day.date).getDate()}/{new Date(day.date).getMonth() + 1}
              </Typography>
              {day.isWeekend && (
                <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                  {day.dayName === 'Sunday' ? 'Sunday' : 'Weekend'}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
      
      {/* Scrollable Container for Staff Rows */}
      <Box sx={scrollableTableStyle}>
        {filteredStaffList.map((staff, index) => {
          const staffShiftsData = staffShifts[staff._id] || {};
          const staffAvailabilitiesData = staffAvailabilities[staff._id] || {};
          
          return (
            <Grid container key={staff._id} className="staff-row print-page">
              <Grid item xs={1.7}>
                <Box sx={{ 
                  p: 2, 
                  borderRight: 1, 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  bgcolor: index % 2 === 0 ? 'grey.50' : 'white',
                  minHeight: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  <Box>
                    <Typography fontWeight="bold">{staff.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {staff.jobtitle}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {staff.speciality || 'General'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {staff.email}
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Email />}
                    onClick={() => handleEmailButtonClick(staff._id)}
                    sx={{ 
                      mt: 1,
                      bgcolor: '#1976d2',
                      '&:hover': {
                        bgcolor: '#1565c0'
                      }
                    }}
                    className="no-print"
                  >
                    Email Schedule
                  </Button>
                </Box>
              </Grid>
              
              {weekInfo.days.map(day => (
                <Grid item xs={1.47} key={`${staff._id}-${day.date}`}>
                  <Box
                    sx={{
                      p: 1,
                      minHeight: '120px',
                      borderRight: 1,
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: day.isWeekend ? 'grey.100' : (index % 2 === 0 ? 'grey.50' : 'white'),
                      cursor: 'pointer'
                    }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, staff._id, day.date)}
                    className="no-print"
                  >
                    {/* Show assigned shifts first */}
                    {staffShiftsData[day.date]?.map((shift, idx) => (
                      <Tooltip 
                        key={`${shift.id}-${idx}`}
                        title={`${shift.categoryOfStaff} - ${shift.ward} - Click to edit, double-click to release`}
                        arrow
                        className="no-print"
                      >
                        <Card 
                          sx={{ 
                            mb: 0.5, 
                            bgcolor: getShiftColor(shift),
                            color: 'white',
                            '&:hover': {
                              boxShadow: 3,
                              transform: 'translateY(-2px)',
                              transition: 'all 0.2s',
                              cursor: 'pointer'
                            }
                          }}
                          onDoubleClick={() => handleShiftDoubleClick(shift, staff._id, day.date)}
                          onClick={(e) => {
                            if (e.ctrlKey || e.metaKey) {
                              handleEditShiftClick(shift, staff._id, day.date);
                            }
                          }}
                          className="shift-card"
                        >
                          <CardContent sx={{ p: 1, position: 'relative' }}>
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.2)',
                                padding: '2px',
                                '&:hover': {
                                  backgroundColor: 'rgba(0,0,0,0.4)',
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditShiftClick(shift, staff._id, day.date);
                              }}
                              className="no-print"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', pr: 3 }}>
                              {shift.startTime} - {shift.endTime}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                              {shift.ward}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block' }}>
                              {shift.categoryOfStaff}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Tooltip>
                    ))}
                    
                    {/* If no assigned shifts, show availabilities */}
                    {(!staffShiftsData[day.date] || staffShiftsData[day.date].length === 0) && 
                     staffAvailabilitiesData[day.date]?.length > 0 && (
                      <>
                        {/* Show unavailability first, then availability */}
                        {staffAvailabilitiesData[day.date]
                          .filter(a => a.isUnavailable || a.isSickLeave)
                          .map((availability, idx) => 
                            renderAvailabilityCard(availability, idx, staff._id, day.date)
                          )}
                          
                        {staffAvailabilitiesData[day.date]
                          .filter(a => !a.isUnavailable && !a.isSickLeave)
                          .map((availability, idx) => 
                            renderAvailabilityCard(availability, idx, staff._id, day.date)
                          )}
                      </>
                    )}
                    
                    {/* If neither shifts nor availabilities, show create option */}
                    {(!staffShiftsData[day.date] || staffShiftsData[day.date].length === 0) && 
                     (!staffAvailabilitiesData[day.date] || staffAvailabilitiesData[day.date].length === 0) && (
                      <Box 
                        sx={{ 
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#999',
                          fontStyle: 'italic',
                          cursor: 'context-menu',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            border: '1px dashed',
                            borderColor: 'primary.main',
                          }
                        }}
                        onClick={(e) => {
                          if (e.detail === 2) {
                            handleOpenCreateAvailability(staff, day);
                          }
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          handleOpenCreateAvailability(staff, day);
                        }}
                        className="no-print"
                      >
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                          No shifts or availability
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                          Double-click: Create availability
                          <br />
                          Right-click: Create availability
                          <br />
                          Drag & drop available shifts
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          );
        })}
        
        {/* No results message */}
        {filteredStaffList.length === 0 && (
          <Grid container>
            <Grid item xs={12}>
              <Box sx={{ 
                p: 4, 
                textAlign: 'center',
                borderBottom: 1,
                borderColor: 'divider'
              }}>
                <Typography variant="body1" color="textSecondary">
                  No staff members match the current filters.
                </Typography>
                <Button 
                  onClick={handleClearFilters}
                  sx={{ mt: 1 }}
                  className="no-print"
                >
                  Clear Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  </Paper>
</Grid>
        
        {/* Available Shifts Panel */}
        <Grid item xs={3} className="no-print">
          <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Available Shifts
              </Typography>
              
              {selectedWardFilter !== 'all' && (
                <Chip 
                  label={`Ward: ${selectedWardFilter}`}
                  size="small"
                  color="primary"
                  onDelete={() => setSelectedWardFilter('all')}
                  sx={{ mb: 1 }}
                />
              )}
              
              <Typography variant="body2" color="textSecondary" paragraph>
                Drag to assign to staff
              </Typography>
            </Box>
            
            <Box sx={{ maxHeight: '600px', overflow: 'auto' }}>
              {filteredAvailableShifts.length === 0 ? (
                <Alert severity={availableShifts.length === 0 ? "info" : "warning"}>
                  {availableShifts.length === 0 
                    ? "No available shifts for this week" 
                    : "No available shifts match the selected ward filter"}
                  {selectedWardFilter !== 'all' && (
                    <Button 
                      size="small" 
                      onClick={() => setSelectedWardFilter('all')}
                      sx={{ ml: 1 }}
                    >
                      Clear Filter
                    </Button>
                  )}
                </Alert>
              ) : (
                filteredAvailableShifts.map((shift, index) => {
                  const shiftDate = new Date(shift.startDateTime);
                  const dayOfWeek = shiftDate.getDay();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  
                  return (
                    <Card
                      key={shift._id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', JSON.stringify(shift));
                        setDraggedItem(shift);
                      }}
                      sx={{ 
                        mb: 2, 
                        cursor: 'grab',
                        bgcolor: isWeekend ? SHIFT_STATUS_COLORS.weekendAvailable : SHIFT_STATUS_COLORS.available,
                        color: 'white',
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          transition: 'transform 0.2s',
                          boxShadow: 3
                        }
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2">
                            {shiftDate.toLocaleDateString()}
                          </Typography>
                          {isWeekend && (
                            <Chip 
                              label="Weekend" 
                              size="small" 
                              sx={{ 
                                backgroundColor: 'rgba(255,255,255,0.3)',
                                color: 'white',
                                fontSize: '0.7rem'
                              }} 
                            />
                          )}
                        </Box>
                        <Typography variant="body2" fontWeight="bold">
                          {shiftDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
                          {new Date(shift.endDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                        <Typography variant="body2">
                          {shift.wardDepartmentArea}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                          {shift.categoryOfStaff || 'Staff'}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          {shift.hospitalLocationId?.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* NEW: Add/Edit Availability Dialog (from StaffAvailability) */}
      <Dialog open={availabilityDialogOpen} onClose={handleCloseAvailabilityDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Availability' : 'Add Availability'}
          <Typography variant="subtitle2" color="textSecondary">
            {selectedDayForAvailability && formatDate(selectedDayForAvailability.date)}
          </Typography>
          {selectedStaffForAvailability && (
            <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
              For: {selectedStaffForAvailability.name} ({selectedStaffForAvailability.jobtitle})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Shift Presets */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WatchLater fontSize="small" />
                Quick Shift Presets
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                  <Grid item xs={6} sm={4} key={key}>
                    <Button
                      fullWidth
                      variant={selectedPreset === key ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handlePresetChange(key)}
                      sx={{ 
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {preset.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
              {selectedPreset && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Selected: {SHIFT_PRESETS[selectedPreset].label}
                </Typography>
              )}
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="datetime-local"
                  name="startDateTime"
                  value={formData.startDateTime}
                  onChange={handleStartDateChange}
                  InputLabelProps={{ shrink: true }}
                  helperText="End time auto-sets to 8 hours later"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="datetime-local"
                  name="endDateTime"
                  value={formData.endDateTime}
                  onChange={handleEndDateChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Category of Staff"
                  name="categoryOfStaff"
                  value={formData.categoryOfStaff}
                  onChange={handleFormChange}
                  helperText={
                    selectedStaffForAvailability 
                      ? `Based on selected staff's job title: ${selectedStaffForAvailability.jobtitle}`
                      : ''
                  }
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ward/Department"
                  name="wardDepartmentArea"
                  value={formData.wardDepartmentArea}
                  onChange={handleFormChange}
                  helperText={
                    selectedStaffForAvailability 
                      ? `Based on selected staff's ward: ${selectedStaffForAvailability.ward}`
                      : ''
                  }
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comments (Optional)"
                  name="comments"
                  value={formData.comments}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                  placeholder="Any additional notes about the availability..."
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isUnavailable}
                      onChange={(e) => {
                        const isUnavailable = e.target.checked;
                        setFormData(prev => ({
                          ...prev,
                          isUnavailable: isUnavailable,
                          unavailabilityReason: isUnavailable ? prev.unavailabilityReason : '',
                          unavailabilityType: isUnavailable ? 'Full Day' : 'Full Day',
                          unavailabilityDetails: isUnavailable ? prev.unavailabilityDetails : '',
                          status: isUnavailable ? 'Approved' : 'Open'
                        }));
                      }}
                      color="error"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        bgcolor: 'error.light',
                        borderRadius: '2px',
                        border: '1px solid #d32f2f'
                      }} />
                      <Typography variant="body2" color={formData.isUnavailable ? 'error.main' : 'text.primary'}>
                        Mark as Unavailable
                      </Typography>
                      {formData.isUnavailable && (
                        <LocalHospital fontSize="small" color="error" />
                      )}
                    </Box>
                  }
                />
              </Grid>

              {formData.isUnavailable && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unavailability Reason</InputLabel>
                      <Select
                        value={formData.unavailabilityReason}
                        label="Unavailability Reason"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          unavailabilityReason: e.target.value
                        }))}
                      >
                        <MenuItem value="">Select Reason...</MenuItem>
                        {UNAVAILABILITY_REASONS.map((reason) => (
                          <MenuItem key={reason} value={reason}>
                            {reason}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Unavailability Type</InputLabel>
                      <Select
                        value={formData.unavailabilityType}
                        label="Unavailability Type"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          unavailabilityType: e.target.value
                        }))}
                      >
                        <MenuItem value="Full Day">Full Day</MenuItem>
                        <MenuItem value="Half Day AM">Half Day (Morning)</MenuItem>
                        <MenuItem value="Half Day PM">Half Day (Afternoon)</MenuItem>
                        <MenuItem value="Partial Day">Partial Day</MenuItem>
                        <MenuItem value="Multi-Day">Multi-Day</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Unavailability Details (Optional)"
                      value={formData.unavailabilityDetails}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        unavailabilityDetails: e.target.value
                      }))}
                      multiline
                      rows={2}
                      placeholder="Additional details about the unavailability..."
                      size="small"
                    />
                  </Grid>
                </>
              )}

              {formData.isSickLeave && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sick Leave Type</InputLabel>
                      <Select
                        value={formData.sickLeaveType}
                        label="Sick Leave Type"
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          sickLeaveType: e.target.value
                        }))}
                      >
                        <MenuItem value="Full Day">Full Day</MenuItem>
                        <MenuItem value="Half Day AM">Half Day (Morning)</MenuItem>
                        <MenuItem value="Half Day PM">Half Day (Afternoon)</MenuItem>
                        <MenuItem value="Partial Day">Partial Day</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Sickness Reason (Optional)"
                      value={formData.sicknessReason}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        sicknessReason: e.target.value
                      }))}
                      placeholder="e.g., Flu, Injury, etc."
                      size="small"
                    />
                  </Grid>
                </>
              )}
              
              {/* Show existing availabilities for this day */}
              {selectedStaffForAvailability && selectedDayForAvailability && 
               staffAvailabilities[selectedStaffForAvailability._id]?.[selectedDayForAvailability.date]?.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Existing availabilities for this day:
                  </Typography>
                  <List dense>
                    {staffAvailabilities[selectedStaffForAvailability._id][selectedDayForAvailability.date]
                      .filter(a => !editMode || a.id !== editingAvailability?.id)
                      .map((availability) => (
                        <ListItem 
                          key={availability.id}
                          sx={{ 
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            mb: 0.5
                          }}
                        >
                          <ListItemText
                            primary={`${availability.startTime} - ${availability.endTime}`}
                            secondary={
                              <span>
                                {formatHoursMinutes(availability.durationMinutes)} • {availability.status}
                                {availability.createdByManager && (
                                  <span style={{ marginLeft: '8px', fontStyle: 'italic', color: '#666' }}>
                                    (Added by manager)
                                  </span>
                                )}
                              </span>
                            }
                          />
                          <ListItemSecondaryAction>
                            <MuiIconButton
                              size="small"
                              onClick={() => handleOpenEditAvailability(availability, selectedStaffForAvailability)}
                            >
                              <Edit fontSize="small" />
                            </MuiIconButton>
                            <MuiIconButton
                              size="small"
                              onClick={(e) => handleDeleteClick(availability.id, e)}
                              sx={{ ml: 0.5 }}
                            >
                              <Delete fontSize="small" />
                            </MuiIconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                  </List>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAvailabilityDialog}>
            Cancel
          </Button>

          {editMode && editingAvailability && (
            <Button 
              onClick={handleDeleteConfirmOpen}
              variant="outlined" 
              color="error"
              startIcon={<Delete />}
              sx={{ mr: 'auto' }}
            >
              Remove Availability
            </Button>
          )}

          <Button 
            onClick={handleFormSubmit} 
            variant="contained" 
            startIcon={editMode ? <Edit /> : <Add />}
          >
            {editMode ? 'Update' : 'Add'} Availability
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteConfirmClose} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Availability</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to remove this availability?
          </Typography>
          {editingAvailability && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Time:</strong> {formatTime(editingAvailability.startDateTime)} - {formatTime(editingAvailability.endDateTime)}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {formatDate(editingAvailability.startDateTime)}
              </Typography>
              {(editingAvailability.isUnavailable || editingAvailability.isSickLeave) && (
                <Typography variant="body2">
                  <strong>Type:</strong> Unavailable ({editingAvailability.unavailabilityReason || editingAvailability.sicknessReason || 'No reason specified'})
                </Typography>
              )}
            </Box>
          )}
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            Note: This action can be reversed by an administrator.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            startIcon={<Delete />}
          >
            Remove Availability
          </Button>
        </DialogActions>
      </Dialog>

      {/* Existing Dialogs (keep your original ones) */}
       {/* Release Shift Confirmation Dialog */}
      <Dialog open={releaseDialogOpen} onClose={handleCancelRelease} maxWidth="sm" fullWidth className="no-print">
        <DialogTitle>Release Shift</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to release this shift?
          </Typography>
          {shiftToRelease && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Staff:</strong> {shiftToRelease.staffName}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {shiftToRelease.shiftDate}
              </Typography>
              <Typography variant="body2">
                <strong>Time:</strong> {shiftToRelease.shiftTime}
              </Typography>
              <Typography variant="body2">
                <strong>Ward:</strong> {shiftToRelease.ward}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontStyle: 'italic' }}>
            This will make the shift available for assignment to another staff member.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRelease}>Cancel</Button>
          <Button 
            onClick={handleReleaseShift} 
            variant="contained" 
            color="warning"
            startIcon={<Delete />}
          >
            Release Shift
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Shift Dialog */}
      {/* Edit Shift Dialog */}
      
<Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="lg" fullWidth className="no-print">
  <DialogTitle>Edit Shift Details</DialogTitle>
  <DialogContent>
    {shiftToEdit && (
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Editing shift for: <strong>{shiftToEdit.staffName}</strong>
        </Typography>
        
        {/* Tabs for Basic Info and Schedule */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ mb: 2 }}
          >
            <Tab label="Basic Information" value="basic" />
            <Tab label="Schedule" value="schedule" />
          </Tabs>
        </Box>
        
        {/* Tab Content */}
        {activeTab === 'basic' && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Top Row: Hospital Location, Category, Reason */}
            <Grid item xs={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Hospital Location</InputLabel>
                <Select
                  name="hospitalLocationId"
                  value={editForm.hospitalLocationId || ''}
                  label="Hospital Location"
                  onChange={handleEditFormChange}
                  size="small"
                >
                  {hospitalLocations?.map(location => (
                    <MenuItem key={location._id} value={location._id}>
                      {location.wardDepartmentArea} ({location.department})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Category of Staff</InputLabel>
                <Select
                  name="categoryOfStaff"
                  value={editForm.categoryOfStaff || ''}
                  label="Category of Staff"
                  onChange={handleEditFormChange}
                  size="small"
                >
                  <MenuItem value="">Select Category...</MenuItem>
                  {CATEGORY_OF_STAFF.map(c => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Reason for Request</InputLabel>
                <Select
                  name="reasonForRequest"
                  value={editForm.reasonForRequest || ''}
                  label="Reason for Request"
                  onChange={handleEditFormChange}
                  size="small"
                >
                  <MenuItem value="">Select Reason...</MenuItem>
                  {REASON_FOR_REQUEST.map(r => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Middle Row: Reason Details */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Request Details"
                name="reasonForRequestDetails"
                value={editForm.reasonForRequestDetails || ''}
                onChange={handleEditFormChange}
                margin="normal"
                multiline
                rows={3}
                placeholder="Provide details about why this shift is needed..."
              />
            </Grid>
            
            {/* Options Row */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Options
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.escort || false}
                          onChange={(e) => setEditForm(prev => ({ ...prev, escort: e.target.checked }))}
                          name="escort"
                        />
                      }
                      label="Escort Required"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.special || false}
                          onChange={(e) => setEditForm(prev => ({ ...prev, special: e.target.checked }))}
                          name="special"
                        />
                      }
                      label="Is this a Special"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.isOvertime || false}
                          onChange={(e) => setEditForm(prev => ({ ...prev, isOvertime: e.target.checked }))}
                          name="isOvertime"
                        />
                      }
                      label="Overtime Shift"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editForm.isActive !== false}
                          onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                          name="isActive"
                        />
                      }
                      label="Active Shift"
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>
                      Uncheck to "delete" the shift (sets isActive=false)
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      label="ECC Approval Number"
                      name="eccNumber"
                      value={editForm.eccNumber || ''}
                      onChange={handleEditFormChange}
                      size="small"
                      placeholder="Enter ECC number..."
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Special Requirements and Comments */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Special Requirements"
                name="specialRequirements"
                value={editForm.specialRequirements || ''}
                onChange={handleEditFormChange}
                margin="normal"
                multiline
                rows={4}
                placeholder="Any special equipment, training, or requirements..."
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Comments"
                name="comments"
                value={editForm.comments || ''}
                onChange={handleEditFormChange}
                margin="normal"
                multiline
                rows={4}
                placeholder="Additional notes or comments..."
              />
            </Grid>
          </Grid>
        )}
        
        {activeTab === 'schedule' && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Shift Time Presets */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Shift Time Preset
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={1}>
                  {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                    <Grid item xs={6} md={4} key={key}>
                      <Paper
                        elevation={editForm.selectedPreset === key ? 2 : 0}
                        sx={{
                          p: 1,
                          cursor: 'pointer',
                          bgcolor: editForm.selectedPreset === key ? 'primary.main' : 'grey.100',
                          color: editForm.selectedPreset === key ? 'white' : 'inherit',
                          '&:hover': {
                            bgcolor: editForm.selectedPreset === key ? 'primary.dark' : 'grey.200',
                          }
                        }}
                        onClick={() => handlePresetSelect(key)}
                      >
                        <Typography variant="body2">
                          {preset.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
            
            {/* Date and Time Fields */}
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                name="startDate"
                value={editForm.startDate || ''}
                onChange={handleEditFormChange}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                size="small"
              />
            </Grid>
            
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="End Date (Same Day)"
                type="date"
                name="endDate"
                value={editForm.endDate || ''}
                onChange={handleEditFormChange}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                size="small"
                helperText="Same day unless overnight shift"
              />
            </Grid>
            
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                name="startTime"
                value={editForm.startTime || ''}
                onChange={handleEditFormChange}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                size="small"
              />
            </Grid>
            
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                name="endTime"
                value={editForm.endTime || ''}
                onChange={handleEditFormChange}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                size="small"
              />
            </Grid>
            
            {/* Shift Duration Display */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>Shift Duration:</strong> {calculateShiftDuration()}
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        )}
      </Box>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCancelEdit}>Cancel</Button>
    <Button 
      onClick={handleUpdateShift} 
      variant="contained" 
      color="primary"
      startIcon={<Edit />}
    >
      Update Shift
    </Button>
  </DialogActions>
</Dialog>
      
      {/* Email Dialog */}
     {/* Email Dialog (for web email fallback) */}
      <Dialog open={emailDialogOpen} onClose={handleCancelEmail} maxWidth="md" fullWidth className="no-print">
        <DialogTitle>Email Weekly Schedule</DialogTitle>
        <DialogContent>
          {selectedStaffForEmail && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Recipient:</strong> {selectedStaffForEmail.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedStaffForEmail.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Week:</strong> {formatDateRange()}
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Email Method:
                  </Typography>
                  <ToggleButtonGroup
                    value={emailMethod}
                    exclusive
                    onChange={(e, newMethod) => newMethod && setEmailMethod(newMethod)}
                    size="small"
                  >
                    <ToggleButton value="outlook">
                      <DesktopWindows sx={{ mr: 1 }} />
                      Outlook
                    </ToggleButton>
                    <ToggleButton value="web">
                      <Language sx={{ mr: 1 }} />
                      Web Email
                    </ToggleButton>
                  </ToggleButtonGroup>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeWeekend}
                        onChange={(e) => setIncludeWeekend(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Include Weekend Days"
                    sx={{ mt: 1, display: 'block' }}
                  />
                </Box>
              </Box>
              
              {emailMethod === 'web' && (
                <>
                  <TextField
                    fullWidth
                    label="Email Subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    margin="normal"
                  />
                  
                  <TextField
                    fullWidth
                    label="Additional Notes (optional)"
                    value={emailContent}
                    onChange={(e) => setEmailContent(e.target.value)}
                    margin="normal"
                    multiline
                    rows={4}
                    helperText="Add any additional notes or instructions for the staff member"
                  />
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" gutterBottom>
                    Schedule Preview:
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#f9f9f9', maxHeight: '200px', overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {generateSchedulePlainText(selectedStaffForEmail.id).slice(0, 500)}...
                    </Typography>
                  </Paper>
                </>
              )}
              
              {emailMethod === 'outlook' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Clicking "Send Email" will open Microsoft Outlook with the schedule pre-populated.
                    If Outlook is not installed, the system will automatically fall back to web email.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEmail}>Cancel</Button>
          <Button 
            onClick={handleSendEmail} 
            variant="contained" 
            color="primary"
            startIcon={emailMethod === 'outlook' ? <DesktopWindows /> : <Send />}
          >
            {emailMethod === 'outlook' ? 'Open in Outlook' : 'Send Web Email'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        className="no-print"
      />
    </Box>
  );
};

export default StaffWeeklyView;